import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { erc20Abi, formatUnits, isAddress, parseUnits } from "viem";
import Nav from "../../components/Nav.jsx";
import Footer from "../../components/Footer.jsx";
import "./toolsdashboard.scss";

const TAB_CONFIG = [
  {
    key: "stake",
    label: "Stake",
    icon: "/icons/stake.svg",
    kicker: "Stake",
    title: "STAKE",
    subtitle: "Staking tools and flows are being prepared.",
  },
  {
    key: "lock",
    label: "Lock",
    icon: "/icons/lock.svg",
    kicker: "Lock",
    title: "LOCK",
    subtitle: "Token lock tools and controls are under construction.",
  },
  {
    key: "lottery",
    label: "Lottery",
    icon: "/icons/lottery.svg",
    kicker: "Lottery",
    title: "LOTTERY",
    subtitle: "Lottery tooling and prize workflows are being prepared.",
  },
  {
    key: "burn",
    label: "Burn",
    icon: "/icons/burn.svg",
    kicker: "Burn",
    title: "BURN",
    subtitle: "Burn dashboards and actions are being built.",
  },
  {
    key: "airdrop",
    label: "Airdrop",
    icon: "/icons/airdrop.svg",
    kicker: "Airdrop",
    title: "AIRDROP",
    subtitle: "Configure and execute batched token distributions on Monad with validation and progress tracking.",
  },
];

const DEFAULT_BURN_CONTRACT_ADDRESS = "0x3217444991Eeb95B17c5B7fA49d40EeABdBe4adA";
const DEFAULT_AIRDROP_CONTRACT_ADDRESS = "0x8a90aabc2593b0D25E9d79AFa4FCCEABB822b00C";
const DEFAULT_AIRDROP_MANAGER_CONTRACT_ADDRESS = "0xc7f0c0B5d1b5E489A2DE812a34b3f73851981D3b";
const DEFAULT_LOCKER_CONTRACT_ADDRESS = "0x4b76253E844E31571EcFC5AB71D1A231560Bf3ad";
const DEFAULT_STAKING_CONTRACT_ADDRESS = "0xA00F5e50A8ddd3Bf6e534a684335C3333f6bE046";
const DEFAULT_AIRDROP_BATCH_SIZE = 250;
const MIN_AIRDROP_BATCH_SIZE = 25;
const TOOLS_API_BASE = (
  process.env.REACT_APP_TOOLS_API_BASE ||
  process.env.REACT_APP_DASHBOARD_API_BASE ||
  "https://api.bobmonad.com"
).replace(/\/+$/, "");
const AIRDROP_ABI = [
  {
    type: "function",
    name: "multisendEqual",
    stateMutability: "nonpayable",
    inputs: [
      { name: "token", type: "address" },
      { name: "recipients", type: "address[]" },
      { name: "amountEach", type: "uint256" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "multisendDifferent",
    stateMutability: "nonpayable",
    inputs: [
      { name: "token", type: "address" },
      { name: "recipients", type: "address[]" },
      { name: "amounts", type: "uint256[]" },
    ],
    outputs: [],
  },
];
const AIRDROP_MANAGER_ABI = [
  {
    type: "function",
    name: "userNextNonce",
    stateMutability: "view",
    inputs: [{ name: "", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "createPlan",
    stateMutability: "payable",
    inputs: [
      { name: "token", type: "address" },
      { name: "totalTokenAmount", type: "uint256" },
      { name: "recipientsCount", type: "uint32" },
      { name: "feeWei", type: "uint256" },
      { name: "deadline", type: "uint64" },
      { name: "signature", type: "bytes" },
    ],
    outputs: [{ name: "planId", type: "uint256" }],
  },
  {
    type: "function",
    name: "executeBatchEqual",
    stateMutability: "nonpayable",
    inputs: [
      { name: "planId", type: "uint256" },
      { name: "recipients", type: "address[]" },
      { name: "amountEach", type: "uint256" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "executeBatchDifferent",
    stateMutability: "nonpayable",
    inputs: [
      { name: "planId", type: "uint256" },
      { name: "recipients", type: "address[]" },
      { name: "amounts", type: "uint256[]" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "finalizePlan",
    stateMutability: "nonpayable",
    inputs: [{ name: "planId", type: "uint256" }],
    outputs: [],
  },
];
const LOCKER_ABI = [
  {
    type: "function",
    name: "createQuickLock",
    stateMutability: "payable",
    inputs: [
      { name: "token", type: "address" },
      { name: "amount", type: "uint256" },
      { name: "unlockTime", type: "uint256" },
      { name: "beneficiary", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "createTimeLock",
    stateMutability: "payable",
    inputs: [
      { name: "token", type: "address" },
      { name: "amount", type: "uint256" },
      { name: "unlockTime", type: "uint256" },
      { name: "beneficiary", type: "address" },
      { name: "transferable", type: "bool" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "createTimeLockBatch",
    stateMutability: "payable",
    inputs: [
      { name: "token", type: "address" },
      { name: "beneficiaries", type: "address[]" },
      { name: "amounts", type: "uint256[]" },
      { name: "unlockTime", type: "uint256" },
      { name: "transferable", type: "bool" },
    ],
    outputs: [{ name: "lockIds", type: "uint256[]" }],
  },
  {
    type: "function",
    name: "tokenFeeBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint16" }],
  },
  {
    type: "function",
    name: "createLockFeeWei",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
];
const DEFAULT_LOTTERY_CONTRACT_ADDRESS = "0x048aec8A762c87c495D08e07d75cA171Ea4864C1";
const STAKING_ABI = [
  {
    type: "function",
    name: "createPool",
    stateMutability: "nonpayable",
    inputs: [
      { name: "stakeToken", type: "address" },
      { name: "rewardToken", type: "address" },
      { name: "rewardAmount", type: "uint256" },
      { name: "startTime", type: "uint64" },
      { name: "durationSeconds", type: "uint64" },
      { name: "useLocks", type: "bool" },
      { name: "lockDurationsSeconds", type: "uint32[]" },
      { name: "bonusBps", type: "uint16[]" },
      { name: "earlyUnstakePenaltyBps", type: "uint16" },
      { name: "penaltyRecipient", type: "address" },
    ],
    outputs: [{ name: "poolId", type: "uint256" }],
  },
];
const LOTTERY_ABI = [
  {
    type: "function",
    name: "createRaffle",
    stateMutability: "payable",
    inputs: [
      { name: "prizeToken", type: "address" },
      { name: "prizeAmount", type: "uint256" },
      { name: "entryToken", type: "address" },
      { name: "entryCost", type: "uint256" },
      { name: "endTime", type: "uint256" },
      { name: "minEntries", type: "uint256" },
      { name: "winnerCount", type: "uint256" },
      { name: "winnerBps", type: "uint16[]" },
      { name: "creatorFeeBps", type: "uint16" },
      { name: "burnFeeBps", type: "uint16" },
      { name: "potFeeBps", type: "uint16" },
      { name: "creatorFeeRecipient", type: "address" },
      { name: "burnRecipient", type: "address" },
    ],
    outputs: [{ name: "raffleId", type: "uint256" }],
  },
];

function getInitialTab() {
  if (typeof window === "undefined") return "stake";
  const raw = new URLSearchParams(window.location.search).get("tab") || "";
  const clean = raw.toLowerCase();
  return TAB_CONFIG.some((t) => t.key === clean) ? clean : "stake";
}

function truncateAddr(addr) {
  if (!addr || addr.length < 10) return addr || "-";
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function formatTokenBalanceDisplay(balanceFormatted, maxDecimals = 2) {
  const raw = String(balanceFormatted || "0").trim();
  if (!raw || raw === "-") return "-";
  const negative = raw.startsWith("-");
  const clean = negative ? raw.slice(1) : raw;
  const [intPartRaw, fracPartRaw = ""] = clean.split(".");
  const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fracPart = fracPartRaw.slice(0, Math.max(0, maxDecimals)).replace(/0+$/, "");
  const result = fracPart ? `${intPart}.${fracPart}` : intPart;
  return negative ? `-${result}` : result;
}

function formatNumberToFixedLocale(valueLike, decimals = 2) {
  const n = Number(String(valueLike ?? "").replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function shortTxHash(hash) {
  const h = String(hash || "");
  if (!h || h.length < 12) return h || "-";
  return `${h.slice(0, 12)}…`;
}

function formatMonDisplay(rawWei, maxDecimals = 9) {
  try {
    const raw = formatUnits(rawWei || 0n, 18);
    const [intPartRaw, fracRaw = ""] = String(raw).split(".");
    const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const frac = fracRaw.slice(0, maxDecimals).replace(/0+$/, "");
    return frac ? `${intPart}.${frac}` : intPart;
  } catch {
    return "-";
  }
}

const TOOL_SUBTAB_LABELS = {
  stake: { list: "Active Pools", create: "Create Pool" },
  lock: { list: "Active Lockers", create: "Create Locker" },
  burn: { list: "Recent Burns", create: "Create Burn" },
  airdrop: { list: "Recent Airdrops", create: "Create Airdrop" },
  lottery: { list: "Active Raffles", create: "Create Raffle" },
};

export default function ToolsDashboardPage() {
  const { address, isConnected, chainId } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const [pageTab, setPageTab] = useState(getInitialTab);
  const [burnTokenAddress, setBurnTokenAddress] = useState("");
  const [burnAmount, setBurnAmount] = useState("");
  const [burnTokenMeta, setBurnTokenMeta] = useState(null);
  const [burnAllowanceRaw, setBurnAllowanceRaw] = useState(0n);
  const [burnLoadingMeta, setBurnLoadingMeta] = useState(false);
  const [burnTxBusy, setBurnTxBusy] = useState(false);
  const [burnError, setBurnError] = useState("");
  const [burnSuccess, setBurnSuccess] = useState("");
  const [airdropTokenAddress, setAirdropTokenAddress] = useState("");
  const [airdropMode, setAirdropMode] = useState("equal");
  const [airdropAmount, setAirdropAmount] = useState("");
  const [airdropRowsText, setAirdropRowsText] = useState("");
  const [airdropTokenMeta, setAirdropTokenMeta] = useState(null);
  const [airdropAllowanceRaw, setAirdropAllowanceRaw] = useState(0n);
  const [airdropLoadingMeta, setAirdropLoadingMeta] = useState(false);
  const [airdropBusy, setAirdropBusy] = useState(false);
  const [airdropBusyLabel, setAirdropBusyLabel] = useState("");
  const [airdropError, setAirdropError] = useState("");
  const [airdropSuccess, setAirdropSuccess] = useState("");
  const [airdropEstimatedTxFeeMonLive, setAirdropEstimatedTxFeeMonLive] = useState(null);
  const [airdropFeeQuote, setAirdropFeeQuote] = useState(null);
  const [airdropPlanQuote, setAirdropPlanQuote] = useState(null);
  const [airdropProgressRows, setAirdropProgressRows] = useState([]);
  const [airdropFirstTxHash, setAirdropFirstTxHash] = useState("");
  const [airdropFileName, setAirdropFileName] = useState("");
  const [airdropActiveLine, setAirdropActiveLine] = useState(1);
  const [airdropScrollTop, setAirdropScrollTop] = useState(0);
  const [airdropLineHeightPx, setAirdropLineHeightPx] = useState(20);
  const [airdropNativeBalanceRaw, setAirdropNativeBalanceRaw] = useState(0n);
  const airdropTextareaRef = useRef(null);
  const airdropGutterRef = useRef(null);
  const [lockType, setLockType] = useState("quick");
  const [lockTokenAddress, setLockTokenAddress] = useState("");
  const [lockTokenMeta, setLockTokenMeta] = useState(null);
  const [lockLoadingMeta, setLockLoadingMeta] = useState(false);
  const [lockUnlockAt, setLockUnlockAt] = useState("");
  const [lockRecipient, setLockRecipient] = useState("");
  const [lockUseConnectedWallet, setLockUseConnectedWallet] = useState(true);
  const [lockTransferable, setLockTransferable] = useState(false);
  const [lockAmount, setLockAmount] = useState("");
  const [lockRowsText, setLockRowsText] = useState("");
  const [lockAllowanceRaw, setLockAllowanceRaw] = useState(0n);
  const [lockTokenFeeBps, setLockTokenFeeBps] = useState(50);
  const [lockCreateFeeWei, setLockCreateFeeWei] = useState(0n);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockError, setLockError] = useState("");
  const [lockSuccess, setLockSuccess] = useState("");
  const [lotteryPrizeToken, setLotteryPrizeToken] = useState("");
  const [lotteryPrizeMeta, setLotteryPrizeMeta] = useState(null);
  const [lotteryPrizeAmount, setLotteryPrizeAmount] = useState("");
  const [lotteryEntryTokenMode, setLotteryEntryTokenMode] = useState("token");
  const [lotteryEntryToken, setLotteryEntryToken] = useState("");
  const [lotteryEntryMeta, setLotteryEntryMeta] = useState(null);
  const [lotteryEntryCost, setLotteryEntryCost] = useState("");
  const [lotteryDurationHours, setLotteryDurationHours] = useState("24");
  const [lotteryWinnerCount, setLotteryWinnerCount] = useState("1");
  const [lotteryWinnerSplits, setLotteryWinnerSplits] = useState("100");
  const [lotteryMinEntries, setLotteryMinEntries] = useState("1");
  const [lotteryCreatorFeeBps, setLotteryCreatorFeeBps] = useState("2500");
  const [lotteryBurnFeeBps, setLotteryBurnFeeBps] = useState("2500");
  const [lotteryPotFeeBps, setLotteryPotFeeBps] = useState("5000");
  const [lotteryCreatorWallet, setLotteryCreatorWallet] = useState("");
  const [lotteryBurnWallet, setLotteryBurnWallet] = useState("0x000000000000000000000000000000000000dEaD");
  const [lotteryUseCreatorWallet, setLotteryUseCreatorWallet] = useState(true);
  const [lotteryPrizeAllowanceRaw, setLotteryPrizeAllowanceRaw] = useState(0n);
  const [lotteryBusy, setLotteryBusy] = useState(false);
  const [lotteryError, setLotteryError] = useState("");
  const [lotterySuccess, setLotterySuccess] = useState("");
  const [toolSubtabs, setToolSubtabs] = useState({
    stake: "list",
    lock: "list",
    burn: "list",
    airdrop: "list",
    lottery: "list",
  });
  const [stakeTokenAddress, setStakeTokenAddress] = useState("");
  const [stakeTokenMeta, setStakeTokenMeta] = useState(null);
  const [stakeRewardAmount, setStakeRewardAmount] = useState("");
  const [stakeDurationDays, setStakeDurationDays] = useState("30");
  const [stakeUseLocks, setStakeUseLocks] = useState(true);
  const [stakeLockTiersText, setStakeLockTiersText] = useState("7:500\n14:1200");
  const [stakePenaltyBps, setStakePenaltyBps] = useState("500");
  const [stakePenaltyRecipient, setStakePenaltyRecipient] = useState("");
  const [stakeUseConnectedRecipient, setStakeUseConnectedRecipient] = useState(true);
  const [stakeMinStake, setStakeMinStake] = useState("");
  const [stakeAllowanceRaw, setStakeAllowanceRaw] = useState(0n);
  const [stakeBusy, setStakeBusy] = useState(false);
  const [stakeError, setStakeError] = useState("");
  const [stakeSuccess, setStakeSuccess] = useState("");

  const BURN_CONTRACT_ADDRESS =
    (process.env.REACT_APP_BOB_BURN_CONTRACT_ADDRESS || DEFAULT_BURN_CONTRACT_ADDRESS).trim();
  const AIRDROP_CONTRACT_ADDRESS =
    (process.env.REACT_APP_BOB_AIRDROP_CONTRACT_ADDRESS || DEFAULT_AIRDROP_CONTRACT_ADDRESS).trim();
  const AIRDROP_MANAGER_CONTRACT_ADDRESS =
    (process.env.REACT_APP_BOB_AIRDROP_MANAGER_CONTRACT_ADDRESS || DEFAULT_AIRDROP_MANAGER_CONTRACT_ADDRESS).trim();
  const EXPECTED_MONAD_CHAIN_ID = Number(process.env.REACT_APP_MONAD_CHAIN_ID || 143);
  const AIRDROP_SERVICE_FEE_PER_RECIPIENT_MON = Number(process.env.REACT_APP_BOB_AIRDROP_SERVICE_FEE_PER_RECIPIENT_MON || 0);
  const AIRDROP_EST_TX_FEE_PER_BATCH_MON = Number(process.env.REACT_APP_BOB_AIRDROP_EST_TX_FEE_PER_BATCH_MON || 0);
  const isWrongNetwork = isConnected && Number(chainId || 0) !== EXPECTED_MONAD_CHAIN_ID;
  const LOCKER_CONTRACT_ADDRESS =
    (process.env.REACT_APP_BOB_LOCKER_CONTRACT_ADDRESS || DEFAULT_LOCKER_CONTRACT_ADDRESS).trim();
  const LOTTERY_CONTRACT_ADDRESS =
    (process.env.REACT_APP_BOB_LOTTERY_CONTRACT_ADDRESS || DEFAULT_LOTTERY_CONTRACT_ADDRESS).trim();
  const STAKING_CONTRACT_ADDRESS =
    (process.env.REACT_APP_BOB_STAKING_CONTRACT_ADDRESS || DEFAULT_STAKING_CONTRACT_ADDRESS).trim();
  const activeToolSubview = pageTab === "airdrop" ? "create" : (toolSubtabs[pageTab] || "create");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", pageTab);
    window.history.replaceState({}, "", `${url.pathname}?${url.searchParams.toString()}${url.hash || ""}`);
  }, [pageTab]);

  useEffect(() => {
    const onPopState = () => setPageTab(getInitialTab());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let rafId = 0;

    const applyNavOffset = () => {
      rafId = 0;
      const root = document.getElementById("bob-tools-page");
      const nav = document.querySelector("nav");
      if (!root || !(nav instanceof HTMLElement)) return;
      const navHeight = Math.ceil(nav.getBoundingClientRect().height);
      root.style.setProperty("--bt-nav-offset", `${navHeight}px`);
    };

    const onMeasure = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(applyNavOffset);
    };

    applyNavOffset();
    window.addEventListener("resize", onMeasure);
    window.addEventListener("scroll", onMeasure, { passive: true });

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onMeasure);
      window.removeEventListener("scroll", onMeasure);
    };
  }, []);

  useEffect(() => {
    const measure = () => {
      const el = airdropTextareaRef.current;
      if (!el) return;
      const lh = Number.parseFloat(window.getComputedStyle(el).lineHeight || "");
      if (Number.isFinite(lh) && lh > 0) {
        setAirdropLineHeightPx(lh);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const activeTab = useMemo(() => TAB_CONFIG.find((t) => t.key === pageTab) || TAB_CONFIG[0], [pageTab]);
  const normalizedBurnToken = burnTokenAddress.trim();
  const burnTokenLooksValid = isAddress(normalizedBurnToken);
  const normalizedAirdropToken = airdropTokenAddress.trim();
  const airdropTokenLooksValid = isAddress(normalizedAirdropToken);
  const airdropExplorerBaseUrl = String(publicClient?.chain?.blockExplorers?.default?.url || "https://monadvision.com").replace(/\/+$/, "");
  const airdropExplorerTxUrl = airdropFirstTxHash ? `${airdropExplorerBaseUrl}/tx/${airdropFirstTxHash}` : "";
  const airdropStatusValue = airdropSuccess
    ? "Completed"
    : airdropBusy
      ? "In Progress"
      : "Not Started";
  const normalizedLockToken = lockTokenAddress.trim();
  const lockTokenLooksValid = isAddress(normalizedLockToken);
  const normalizedStakeToken = stakeTokenAddress.trim();
  const stakeTokenLooksValid = isAddress(normalizedStakeToken);
  const normalizedLotteryPrizeToken = lotteryPrizeToken.trim();
  const normalizedLotteryEntryToken = lotteryEntryToken.trim();
  const lotteryPrizeTokenValid = isAddress(normalizedLotteryPrizeToken);
  const lotteryEntryTokenValid = lotteryEntryTokenMode === "native" || isAddress(normalizedLotteryEntryToken);

  const burnAmountRaw = useMemo(() => {
    if (!burnTokenMeta || !burnAmount) return null;
    try {
      return parseUnits(burnAmount, burnTokenMeta.decimals);
    } catch {
      return null;
    }
  }, [burnAmount, burnTokenMeta]);

  const needsApproval = useMemo(() => {
    if (!burnAmountRaw) return false;
    return burnAmountRaw > burnAllowanceRaw;
  }, [burnAmountRaw, burnAllowanceRaw]);

  const burnButtonDisabled =
    !isConnected ||
    !walletClient ||
    !BURN_CONTRACT_ADDRESS ||
    !burnTokenMeta ||
    !burnAmountRaw ||
    burnAmountRaw <= 0n ||
    burnTxBusy;

  const parsedAirdropInput = useMemo(() => {
    const lines = airdropRowsText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    const items = lines.map((line, idx) => {
      const [addrPart, amountPart] = line.split(",").map((p) => (p || "").trim());
      const errors = [];
      if (!isAddress(addrPart, { strict: false })) errors.push("Invalid address format");
      if (airdropMode === "different") {
        if (!amountPart) {
          errors.push("Missing amount");
        } else if (airdropTokenMeta) {
          try {
            const raw = parseUnits(amountPart, airdropTokenMeta.decimals);
            if (raw <= 0n) errors.push("Amount must be > 0");
          } catch {
            errors.push("Invalid amount format");
          }
        }
      }
      return {
        rowNumber: idx + 1,
        line,
        address: addrPart,
        amountText: amountPart || "",
        normalized: isAddress(addrPart, { strict: false }) ? addrPart.toLowerCase() : "",
        errors,
      };
    });

    const freq = new Map();
    for (const it of items) {
      if (!it.normalized) continue;
      freq.set(it.normalized, (freq.get(it.normalized) || 0) + 1);
    }
    const duplicateAddresses = new Set(
      [...freq.entries()].filter(([, count]) => count > 1).map(([addr]) => addr)
    );
    for (const it of items) {
      if (it.normalized && duplicateAddresses.has(it.normalized)) {
        it.errors.push("Duplicate address");
      }
    }

    const validRows = items
      .filter((it) => it.errors.length === 0)
      .map((it) => ({ address: it.address, amountText: it.amountText }));

    const invalidCount = items.filter((it) => it.errors.length > 0).length;
    return {
      items,
      validRows,
      invalidCount,
      duplicateCount: duplicateAddresses.size,
    };
  }, [airdropRowsText, airdropMode, airdropTokenMeta]);

  const airdropCallData = useMemo(() => {
    if (!airdropTokenMeta) return { recipients: [], amountsRaw: [], totalRaw: 0n, amountEachRaw: null };
    const recipients = parsedAirdropInput.validRows.map((r) => r.address);
    if (!recipients.length) return { recipients, amountsRaw: [], totalRaw: 0n, amountEachRaw: null };

    if (airdropMode === "equal") {
      try {
        const amountEachRaw = parseUnits(airdropAmount || "0", airdropTokenMeta.decimals);
        const totalRaw = amountEachRaw * parseUnits(String(recipients.length), 0);
        return { recipients, amountsRaw: [], totalRaw, amountEachRaw };
      } catch {
        return { recipients, amountsRaw: [], totalRaw: 0n, amountEachRaw: null };
      }
    }

    const amountsRaw = [];
    let totalRaw = 0n;
    for (const row of parsedAirdropInput.validRows) {
      try {
        const raw = parseUnits(row.amountText || "0", airdropTokenMeta.decimals);
        if (raw <= 0n) return { recipients, amountsRaw: [], totalRaw: 0n, amountEachRaw: null };
        amountsRaw.push(raw);
        totalRaw += raw;
      } catch {
        return { recipients, amountsRaw: [], totalRaw: 0n, amountEachRaw: null };
      }
    }
    return { recipients, amountsRaw, totalRaw, amountEachRaw: null };
  }, [airdropTokenMeta, parsedAirdropInput.validRows, airdropMode, airdropAmount]);

  const airdropNeedsApproval = useMemo(() => (
    airdropCallData.totalRaw > airdropAllowanceRaw
  ), [airdropCallData.totalRaw, airdropAllowanceRaw]);
  const airdropInsufficientBalance = useMemo(() => {
    if (!airdropTokenMeta) return false;
    return (airdropCallData.totalRaw || 0n) > (airdropTokenMeta.balanceRaw || 0n);
  }, [airdropCallData.totalRaw, airdropTokenMeta]);

  const airdropEnteredTotalPreview = useMemo(() => {
    const parseLooseAmount = (raw) => {
      const cleaned = String(raw || "")
        .replace(/,/g, "")
        .trim();
      const n = Number(cleaned);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    const sumRowAmounts = () => {
      let total = 0;
      for (const item of parsedAirdropInput.items) {
        total += parseLooseAmount(item.amountText);
      }
      return total;
    };

    if (airdropTokenMeta) {
      if (parsedAirdropInput.validRows.length === 0) {
        return `- ${airdropTokenMeta.symbol}`;
      }
      const totalText = formatUnits(airdropCallData.totalRaw || 0n, airdropTokenMeta.decimals);
      return `${formatNumberToFixedLocale(totalText, 2)} ${airdropTokenMeta.symbol}`;
    }

    const rowTotal = sumRowAmounts();
    if (rowTotal > 0) return `${rowTotal}`;

    if (airdropMode === "different") {
      return "-";
    }

    const each = Number(airdropAmount || "");
    const count = parsedAirdropInput.validRows.length;
    if (Number.isFinite(each) && each > 0 && count > 0) {
      return `${each * count}`;
    }
    return "-";
  }, [
    airdropTokenMeta,
    airdropCallData.totalRaw,
    airdropMode,
    parsedAirdropInput.items,
    parsedAirdropInput.validRows.length,
    airdropAmount,
  ]);

  const airdropManagerReady = isAddress(AIRDROP_MANAGER_CONTRACT_ADDRESS);
  const airdropDisabled =
    !isConnected ||
    isWrongNetwork ||
    !walletClient ||
    !isAddress(AIRDROP_CONTRACT_ADDRESS) ||
    !airdropManagerReady ||
    !airdropTokenMeta ||
    parsedAirdropInput.invalidCount > 0 ||
    parsedAirdropInput.duplicateCount > 0 ||
    !airdropCallData.recipients.length ||
    !airdropPlanQuote?.signature ||
    airdropCallData.totalRaw <= 0n ||
    airdropInsufficientBalance ||
    airdropBusy;
  const airdropCanSubmit = !airdropDisabled && !airdropBusy;
  const airdropEstimatedBatchCount = useMemo(() => {
    const count = airdropCallData.recipients.length;
    if (!count) return 0;
    return Math.ceil(count / DEFAULT_AIRDROP_BATCH_SIZE);
  }, [airdropCallData.recipients.length]);
  useEffect(() => {
    const recipients = airdropCallData.recipients.length;
    if (!recipients) {
      setAirdropFeeQuote(null);
      return;
    }
    const ctrl = new AbortController();
    const run = async () => {
      try {
        const url = `${TOOLS_API_BASE}/api/tools/airdrop-fee-quote?recipients=${recipients}`;
        const res = await fetch(url, { signal: ctrl.signal });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) throw new Error(json?.error || `Fee quote failed (${res.status})`);
        setAirdropFeeQuote(json);
      } catch {
        setAirdropFeeQuote(null);
      }
    };
    run();
    return () => ctrl.abort();
  }, [airdropCallData.recipients.length]);

  const airdropServiceFeeMon = useMemo(() => {
    const quoted = Number(airdropFeeQuote?.serviceFeeMon || 0);
    if (Number.isFinite(quoted) && quoted > 0) return quoted;
    return Math.max(0, AIRDROP_SERVICE_FEE_PER_RECIPIENT_MON) * airdropCallData.recipients.length;
  }, [airdropFeeQuote, AIRDROP_SERVICE_FEE_PER_RECIPIENT_MON, airdropCallData.recipients.length]);
  const airdropServiceFeeUsd = useMemo(() => {
    const fromPlan = Number(airdropPlanQuote?.serviceFeeUsd || 0);
    if (Number.isFinite(fromPlan) && fromPlan > 0) return fromPlan;
    const fromFeeQuote = Number(airdropFeeQuote?.serviceFeeUsd || 0);
    if (Number.isFinite(fromFeeQuote) && fromFeeQuote > 0) return fromFeeQuote;
    return 0;
  }, [airdropPlanQuote, airdropFeeQuote]);

  useEffect(() => {
    const canQuotePlan =
      !!publicClient &&
      !!address &&
      airdropManagerReady &&
      isAddress(normalizedAirdropToken) &&
      airdropCallData.recipients.length > 0 &&
      airdropCallData.totalRaw > 0n;
    if (!canQuotePlan) {
      setAirdropPlanQuote(null);
      return;
    }

    const ctrl = new AbortController();
    const run = async () => {
      try {
        const nonce = await publicClient.readContract({
          address: AIRDROP_MANAGER_CONTRACT_ADDRESS,
          abi: AIRDROP_MANAGER_ABI,
          functionName: "userNextNonce",
          args: [address],
        });
        const url = new URL(`${TOOLS_API_BASE}/api/tools/airdrop-plan-quote`);
        url.searchParams.set("user", address);
        url.searchParams.set("token", normalizedAirdropToken);
        url.searchParams.set("totalTokenAmountRaw", String(airdropCallData.totalRaw));
        url.searchParams.set("recipients", String(airdropCallData.recipients.length));
        url.searchParams.set("nonce", String(nonce));
        const res = await fetch(url.toString(), { signal: ctrl.signal });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) throw new Error(json?.error || `Plan quote failed (${res.status})`);
        setAirdropPlanQuote(json);
      } catch {
        setAirdropPlanQuote(null);
      }
    };
    run();
    return () => ctrl.abort();
  }, [
    publicClient,
    address,
    airdropManagerReady,
    normalizedAirdropToken,
    airdropCallData.recipients.length,
    airdropCallData.totalRaw,
  ]);
  useEffect(() => {
    let cancelled = false;

    async function estimateAirdropTxFees() {
      if (
        !publicClient ||
        !isConnected ||
        !address ||
        !isAddress(AIRDROP_CONTRACT_ADDRESS) ||
        !airdropTokenMeta ||
        !isAddress(normalizedAirdropToken) ||
        airdropCallData.recipients.length === 0
      ) {
        setAirdropEstimatedTxFeeMonLive(null);
        return;
      }

      try {
        const gasPriceWei = await publicClient.getGasPrice();
        const recipientCount = airdropCallData.recipients.length;
        const batchCount = Math.ceil(recipientCount / DEFAULT_AIRDROP_BATCH_SIZE);
        const recipientsPerBatchAvg = batchCount > 0 ? recipientCount / batchCount : 0;
        const fallbackGasPerBatch =
          airdropMode === "equal"
            ? Math.round(120000 + recipientsPerBatchAvg * 65000)
            : Math.round(130000 + recipientsPerBatchAvg * 70000);
        const fallbackTotalGasWei = parseUnits(String(Math.max(0, fallbackGasPerBatch * batchCount)), 0) * gasPriceWei;
        const fallbackWithBufferWei = (fallbackTotalGasWei * 120n) / 100n;
        const fallbackMon = Number(formatUnits(fallbackWithBufferWei, 18));
        let totalGasWei = 0n;
        let usedFallback = false;

        for (let start = 0; start < recipientCount; start += DEFAULT_AIRDROP_BATCH_SIZE) {
          const end = Math.min(start + DEFAULT_AIRDROP_BATCH_SIZE, recipientCount);
          const recipientsBatch = airdropCallData.recipients.slice(start, end);
          if (!recipientsBatch.length) continue;

          let estimatedGas = 0n;
          if (airdropMode === "equal") {
            if (!airdropCallData.amountEachRaw || airdropCallData.amountEachRaw <= 0n) {
              usedFallback = true;
              totalGasWei = 0n;
              break;
            }
            estimatedGas = await publicClient.estimateContractGas({
              address: AIRDROP_CONTRACT_ADDRESS,
              abi: AIRDROP_ABI,
              functionName: "multisendEqual",
              args: [normalizedAirdropToken, recipientsBatch, airdropCallData.amountEachRaw],
              account: address,
            });
          } else {
            const amountsBatch = airdropCallData.amountsRaw.slice(start, end);
            if (!amountsBatch.length || amountsBatch.length !== recipientsBatch.length) {
              usedFallback = true;
              totalGasWei = 0n;
              break;
            }
            estimatedGas = await publicClient.estimateContractGas({
              address: AIRDROP_CONTRACT_ADDRESS,
              abi: AIRDROP_ABI,
              functionName: "multisendDifferent",
              args: [normalizedAirdropToken, recipientsBatch, amountsBatch],
              account: address,
            });
          }

          totalGasWei += estimatedGas * gasPriceWei;
        }

        const withBufferWei = (totalGasWei * 115n) / 100n;
        const asMon = Number(formatUnits(withBufferWei, 18));
        const finalMon = usedFallback
          ? fallbackMon
          : (Number.isFinite(asMon) && asMon > 0 ? asMon : fallbackMon);
        if (!cancelled) {
          setAirdropEstimatedTxFeeMonLive(Number.isFinite(finalMon) && finalMon >= 0 ? finalMon : null);
        }
      } catch {
        try {
          const gasPriceWei = await publicClient.getGasPrice();
          const recipientCount = airdropCallData.recipients.length;
          const batchCount = Math.ceil(recipientCount / DEFAULT_AIRDROP_BATCH_SIZE);
          const recipientsPerBatchAvg = batchCount > 0 ? recipientCount / batchCount : 0;
          const fallbackGasPerBatch =
            airdropMode === "equal"
              ? Math.round(120000 + recipientsPerBatchAvg * 65000)
              : Math.round(130000 + recipientsPerBatchAvg * 70000);
          const fallbackTotalGasWei = parseUnits(String(Math.max(0, fallbackGasPerBatch * batchCount)), 0) * gasPriceWei;
          const fallbackWithBufferWei = (fallbackTotalGasWei * 120n) / 100n;
          const fallbackMon = Number(formatUnits(fallbackWithBufferWei, 18));
          if (!cancelled) {
            setAirdropEstimatedTxFeeMonLive(Number.isFinite(fallbackMon) && fallbackMon >= 0 ? fallbackMon : null);
          }
        } catch {
          if (!cancelled) setAirdropEstimatedTxFeeMonLive(null);
        }
      }
    }

    estimateAirdropTxFees();
    return () => {
      cancelled = true;
    };
  }, [
    publicClient,
    isConnected,
    address,
    AIRDROP_CONTRACT_ADDRESS,
    airdropTokenMeta,
    normalizedAirdropToken,
    airdropCallData.recipients,
    airdropCallData.amountEachRaw,
    airdropCallData.amountsRaw,
    airdropMode,
  ]);

  const airdropEstimatedTxFeeMon = useMemo(() => {
    if (Number.isFinite(airdropEstimatedTxFeeMonLive) && airdropEstimatedTxFeeMonLive >= 0) {
      return airdropEstimatedTxFeeMonLive;
    }
    return Math.max(0, AIRDROP_EST_TX_FEE_PER_BATCH_MON) * airdropEstimatedBatchCount;
  }, [airdropEstimatedTxFeeMonLive, AIRDROP_EST_TX_FEE_PER_BATCH_MON, airdropEstimatedBatchCount]);
  const showAirdropFeeValues = airdropCallData.recipients.length > 0;

  const lockEffectiveRecipient = useMemo(() => (
    lockUseConnectedWallet && address ? address : lockRecipient.trim()
  ), [lockUseConnectedWallet, address, lockRecipient]);

  const lockParsedRows = useMemo(() => {
    const lines = lockRowsText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const rows = [];
    for (const line of lines) {
      const [addrPart, amtPart] = line.split(",").map((p) => (p || "").trim());
      if (!isAddress(addrPart)) continue;
      rows.push({ address: addrPart, amountText: amtPart || "" });
    }
    return rows;
  }, [lockRowsText]);

  const lockUnlockTimestamp = useMemo(() => {
    if (!lockUnlockAt) return null;
    const ms = Date.parse(lockUnlockAt);
    if (!Number.isFinite(ms)) return null;
    return Math.floor(ms / 1000);
  }, [lockUnlockAt]);

  const lockCalc = useMemo(() => {
    if (!lockTokenMeta) return { totalRaw: 0n, totalText: "-", count: 0, amountRaw: null, batchRecipients: [], batchAmounts: [] };
    if (lockType === "quick") {
      try {
        const amountRaw = parseUnits(lockAmount || "0", lockTokenMeta.decimals);
        return {
          totalRaw: amountRaw,
          totalText: `${lockAmount || "0"} ${lockTokenMeta.symbol}`,
          count: lockEffectiveRecipient && isAddress(lockEffectiveRecipient) ? 1 : 0,
          amountRaw,
          batchRecipients: [],
          batchAmounts: [],
        };
      } catch {
        return { totalRaw: 0n, totalText: "-", count: 0, amountRaw: null, batchRecipients: [], batchAmounts: [] };
      }
    }
    if (lockParsedRows.length > 0) {
      const amounts = [];
      let totalRaw = 0n;
      for (const row of lockParsedRows) {
        try {
          const amt = parseUnits(row.amountText || "0", lockTokenMeta.decimals);
          if (amt <= 0n) return { totalRaw: 0n, totalText: "-", count: lockParsedRows.length, amountRaw: null, batchRecipients: [], batchAmounts: [] };
          amounts.push(amt);
          totalRaw += amt;
        } catch {
          return { totalRaw: 0n, totalText: "-", count: lockParsedRows.length, amountRaw: null, batchRecipients: [], batchAmounts: [] };
        }
      }
      return {
        totalRaw,
        totalText: `${formatUnits(totalRaw, lockTokenMeta.decimals)} ${lockTokenMeta.symbol}`,
        count: lockParsedRows.length,
        amountRaw: null,
        batchRecipients: lockParsedRows.map((r) => r.address),
        batchAmounts: amounts,
      };
    }
    try {
      const amountRaw = parseUnits(lockAmount || "0", lockTokenMeta.decimals);
      const recipientOk = lockEffectiveRecipient && isAddress(lockEffectiveRecipient);
      return {
        totalRaw: amountRaw,
        totalText: `${lockAmount || "0"} ${lockTokenMeta.symbol}`,
        count: recipientOk ? 1 : 0,
        amountRaw,
        batchRecipients: recipientOk ? [lockEffectiveRecipient] : [],
        batchAmounts: recipientOk ? [amountRaw] : [],
      };
    } catch {
      return { totalRaw: 0n, totalText: "-", count: 0, amountRaw: null, batchRecipients: [], batchAmounts: [] };
    }
  }, [lockTokenMeta, lockType, lockAmount, lockEffectiveRecipient, lockParsedRows]);

  const lockTokenFeeRaw = useMemo(() => {
    const bpsRaw = parseUnits(String(lockTokenFeeBps), 0);
    return (lockCalc.totalRaw * bpsRaw) / 10000n;
  }, [lockCalc.totalRaw, lockTokenFeeBps]);

  const lockRequiredAllowanceRaw = useMemo(() => (
    lockCalc.totalRaw + lockTokenFeeRaw
  ), [lockCalc.totalRaw, lockTokenFeeRaw]);

  const lockNeedsApproval = useMemo(() => (
    lockRequiredAllowanceRaw > lockAllowanceRaw
  ), [lockRequiredAllowanceRaw, lockAllowanceRaw]);

  const lockDisabled =
    !isConnected ||
    !walletClient ||
    !isAddress(LOCKER_CONTRACT_ADDRESS) ||
    !lockTokenMeta ||
    !lockUnlockTimestamp ||
    lockUnlockTimestamp <= Math.floor(Date.now() / 1000) ||
    lockCalc.totalRaw <= 0n ||
    lockCalc.count <= 0 ||
    lockBusy;

  const lockNativeFeeDisplay = useMemo(() => {
    if (!lockCreateFeeWei) return "0 MON";
    return `${formatUnits(lockCreateFeeWei, 18)} MON`;
  }, [lockCreateFeeWei]);
  const lotteryWinnerSplitArray = useMemo(() => (
    lotteryWinnerSplits
      .split(",")
      .map((v) => Number((v || "").trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
  ), [lotteryWinnerSplits]);
  const lotteryWinnerSplitSum = useMemo(() => lotteryWinnerSplitArray.reduce((a, b) => a + b, 0), [lotteryWinnerSplitArray]);
  const lotteryWinnerCountNum = Number(lotteryWinnerCount || 0);
  const lotteryDurationNum = Number(lotteryDurationHours || 0);
  const lotteryEndTs = useMemo(() => Math.floor(Date.now() / 1000) + Math.max(0, Math.floor(lotteryDurationNum * 3600)), [lotteryDurationNum]);
  const lotteryPrizeAmountRaw = useMemo(() => {
    if (!lotteryPrizeMeta) return 0n;
    try { return parseUnits(lotteryPrizeAmount || "0", lotteryPrizeMeta.decimals); } catch { return 0n; }
  }, [lotteryPrizeAmount, lotteryPrizeMeta]);
  const lotteryEntryCostRaw = useMemo(() => {
    if (lotteryEntryTokenMode === "native") {
      try { return parseUnits(lotteryEntryCost || "0", 18); } catch { return 0n; }
    }
    if (!lotteryEntryMeta) return 0n;
    try { return parseUnits(lotteryEntryCost || "0", lotteryEntryMeta.decimals); } catch { return 0n; }
  }, [lotteryEntryCost, lotteryEntryMeta, lotteryEntryTokenMode]);
  const lotteryCreatorFee = Number(lotteryCreatorFeeBps || 0);
  const lotteryBurnFee = Number(lotteryBurnFeeBps || 0);
  const lotteryPotFee = Number(lotteryPotFeeBps || 0);
  const lotteryFeesSum = lotteryCreatorFee + lotteryBurnFee + lotteryPotFee;
  const lotteryCreatorRecipient = lotteryUseCreatorWallet && address ? address : lotteryCreatorWallet.trim();
  const lotteryNeedsApprovePrize = useMemo(() => (
    lotteryPrizeAmountRaw > lotteryPrizeAllowanceRaw
  ), [lotteryPrizeAmountRaw, lotteryPrizeAllowanceRaw]);
  const lotteryDisabled =
    !isConnected ||
    !walletClient ||
    !isAddress(LOTTERY_CONTRACT_ADDRESS) ||
    !lotteryPrizeMeta ||
    lotteryPrizeAmountRaw <= 0n ||
    !lotteryEntryTokenValid ||
    lotteryEntryCostRaw <= 0n ||
    lotteryWinnerCountNum <= 0 ||
    lotteryWinnerSplitArray.length !== lotteryWinnerCountNum ||
    lotteryWinnerSplitSum !== 100 ||
    lotteryDurationNum <= 0 ||
    lotteryFeesSum !== 10000 ||
    !isAddress(lotteryCreatorRecipient || "") ||
    !isAddress(lotteryBurnWallet || "") ||
    lotteryBusy;

  const stakeTierPairs = useMemo(() => (
    stakeLockTiersText
      .split(/\r?\n/)
      .map((row) => row.trim())
      .filter(Boolean)
      .map((row) => {
        const [daysPart, bonusPart] = row.split(":").map((v) => (v || "").trim());
        const days = Number(daysPart);
        const bonusBps = Number(bonusPart);
        return { days, bonusBps };
      })
      .filter((t) => Number.isFinite(t.days) && t.days > 0 && Number.isFinite(t.bonusBps) && t.bonusBps >= 0 && t.bonusBps <= 10000)
  ), [stakeLockTiersText]);
  const stakeRewardAmountRaw = useMemo(() => {
    if (!stakeTokenMeta) return 0n;
    try {
      return parseUnits(stakeRewardAmount || "0", stakeTokenMeta.decimals);
    } catch {
      return 0n;
    }
  }, [stakeRewardAmount, stakeTokenMeta]);
  const stakeDurationSeconds = useMemo(() => {
    const days = Number(stakeDurationDays || 0);
    if (!Number.isFinite(days) || days <= 0) return 0;
    return Math.floor(days * 24 * 60 * 60);
  }, [stakeDurationDays]);
  const stakePenaltyRecipientResolved = stakeUseConnectedRecipient && address ? address : stakePenaltyRecipient.trim();
  const stakePenaltyBpsNum = Number(stakePenaltyBps || 0);
  const stakeNeedsApproval = useMemo(() => (
    stakeRewardAmountRaw > stakeAllowanceRaw
  ), [stakeRewardAmountRaw, stakeAllowanceRaw]);
  const stakeDisabled =
    !isConnected ||
    !walletClient ||
    !isAddress(STAKING_CONTRACT_ADDRESS) ||
    !stakeTokenLooksValid ||
    !stakeTokenMeta ||
    stakeRewardAmountRaw <= 0n ||
    stakeDurationSeconds <= 0 ||
    (stakeUseLocks ? stakeTierPairs.length === 0 : false) ||
    (!stakeUseLocks && (stakePenaltyBpsNum < 0 || stakePenaltyBpsNum > 10000 || !isAddress(stakePenaltyRecipientResolved || ""))) ||
    stakeBusy;

  const refreshBurnTokenMeta = useCallback(async (tokenAddress) => {
    if (!publicClient || !tokenAddress || !address || !BURN_CONTRACT_ADDRESS) return;
    setBurnLoadingMeta(true);
    setBurnError("");
    try {
      const [symbol, name, decimals, balance, allowance] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "name" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "decimals" }),
        publicClient.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [address],
        }),
        publicClient.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "allowance",
          args: [address, BURN_CONTRACT_ADDRESS],
        }),
      ]);

      setBurnTokenMeta({
        symbol,
        name,
        decimals,
        balanceRaw: balance,
        balanceFormatted: formatUnits(balance, decimals),
      });
      setBurnAllowanceRaw(allowance);
    } catch (err) {
      setBurnTokenMeta(null);
      setBurnAllowanceRaw(0n);
      setBurnError(err?.shortMessage || err?.message || "Failed to read token metadata.");
    } finally {
      setBurnLoadingMeta(false);
    }
  }, [publicClient, address, BURN_CONTRACT_ADDRESS]);

  const refreshAirdropTokenMeta = useCallback(async (tokenAddress) => {
    if (!publicClient || !tokenAddress || !address || !AIRDROP_CONTRACT_ADDRESS) return;
    setAirdropLoadingMeta(true);
    setAirdropError("");
    try {
      const [symbol, name, decimals, balance, allowance] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "name" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "decimals" }),
        publicClient.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [address],
        }),
        publicClient.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "allowance",
          args: [address, AIRDROP_MANAGER_CONTRACT_ADDRESS],
        }),
      ]);
      setAirdropTokenMeta({
        symbol,
        name,
        decimals,
        balanceRaw: balance,
        balanceFormatted: formatUnits(balance, decimals),
      });
      setAirdropAllowanceRaw(allowance);
    } catch (err) {
      setAirdropTokenMeta(null);
      setAirdropAllowanceRaw(0n);
      setAirdropError(err?.shortMessage || err?.message || "Failed to read token metadata.");
    } finally {
      setAirdropLoadingMeta(false);
    }
  }, [publicClient, address, AIRDROP_MANAGER_CONTRACT_ADDRESS]);

  const refreshLockTokenMeta = useCallback(async (tokenAddress) => {
    if (!publicClient || !tokenAddress || !address || !LOCKER_CONTRACT_ADDRESS) return;
    setLockLoadingMeta(true);
    setLockError("");
    try {
      const [symbol, name, decimals, balance, allowance, tokenFee, lockFee] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "name" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "decimals" }),
        publicClient.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [address],
        }),
        publicClient.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "allowance",
          args: [address, LOCKER_CONTRACT_ADDRESS],
        }),
        publicClient.readContract({
          address: LOCKER_CONTRACT_ADDRESS,
          abi: LOCKER_ABI,
          functionName: "tokenFeeBps",
        }),
        publicClient.readContract({
          address: LOCKER_CONTRACT_ADDRESS,
          abi: LOCKER_ABI,
          functionName: "createLockFeeWei",
        }),
      ]);

      setLockTokenMeta({
        symbol,
        name,
        decimals,
        balanceRaw: balance,
        balanceFormatted: formatUnits(balance, decimals),
      });
      setLockAllowanceRaw(allowance);
      setLockTokenFeeBps(Number(tokenFee));
      setLockCreateFeeWei(lockFee);
    } catch (err) {
      setLockTokenMeta(null);
      setLockAllowanceRaw(0n);
      setLockError(err?.shortMessage || err?.message || "Failed to read token metadata.");
    } finally {
      setLockLoadingMeta(false);
    }
  }, [publicClient, address, LOCKER_CONTRACT_ADDRESS]);

  const refreshLotteryPrizeMeta = useCallback(async (tokenAddress) => {
    if (!publicClient || !tokenAddress || !address || !LOTTERY_CONTRACT_ADDRESS) return;
    setLotteryError("");
    try {
      const [symbol, name, decimals, balance, allowance] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "name" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "decimals" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "balanceOf", args: [address] }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "allowance", args: [address, LOTTERY_CONTRACT_ADDRESS] }),
      ]);
      setLotteryPrizeMeta({ symbol, name, decimals, balanceRaw: balance, balanceFormatted: formatUnits(balance, decimals) });
      setLotteryPrizeAllowanceRaw(allowance);
    } catch (err) {
      setLotteryPrizeMeta(null);
      setLotteryPrizeAllowanceRaw(0n);
      setLotteryError(err?.shortMessage || err?.message || "Failed to read prize token metadata.");
    }
  }, [publicClient, address, LOTTERY_CONTRACT_ADDRESS]);

  const refreshLotteryEntryMeta = useCallback(async (tokenAddress) => {
    if (!publicClient || !tokenAddress || !address) return;
    try {
      const [symbol, name, decimals, balance] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "name" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "decimals" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "balanceOf", args: [address] }),
      ]);
      setLotteryEntryMeta({ symbol, name, decimals, balanceRaw: balance, balanceFormatted: formatUnits(balance, decimals) });
    } catch {
      setLotteryEntryMeta(null);
    }
  }, [publicClient, address]);

  const refreshStakeTokenMeta = useCallback(async (tokenAddress) => {
    if (!publicClient || !tokenAddress || !address || !isAddress(STAKING_CONTRACT_ADDRESS)) return;
    setStakeError("");
    try {
      const [symbol, name, decimals, balance, allowance] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "name" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "decimals" }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "balanceOf", args: [address] }),
        publicClient.readContract({ address: tokenAddress, abi: erc20Abi, functionName: "allowance", args: [address, STAKING_CONTRACT_ADDRESS] }),
      ]);
      setStakeTokenMeta({ symbol, name, decimals, balanceRaw: balance, balanceFormatted: formatUnits(balance, decimals) });
      setStakeAllowanceRaw(allowance);
    } catch (err) {
      setStakeTokenMeta(null);
      setStakeAllowanceRaw(0n);
      setStakeError(err?.shortMessage || err?.message || "Failed to read staking token metadata.");
    }
  }, [publicClient, address, STAKING_CONTRACT_ADDRESS]);

  async function handleAirdropFileUpload(event) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setAirdropFileName(file.name || "");
    if (file.size > 2 * 1024 * 1024) {
      setAirdropError("File too large. Max size is 2MB.");
      return;
    }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["csv", "txt"].includes(ext)) {
      setAirdropError("Unsupported file type. Use CSV or TXT.");
      return;
    }
    try {
      const text = await file.text();
      setAirdropRowsText(text.replace(/;/g, ","));
    } catch {
      setAirdropError("Could not read file.");
    }
  }

  const airdropLineCount = useMemo(() => {
    const rows = airdropRowsText.split(/\r?\n/).length;
    return Math.max(1, rows);
  }, [airdropRowsText]);

  const airdropRawLines = useMemo(() => airdropRowsText.split(/\r?\n/), [airdropRowsText]);

  const airdropActiveLineHasError = useMemo(() => {
    const activeItem = parsedAirdropInput.items.find((it) => it.rowNumber === airdropActiveLine);
    return !!activeItem?.errors?.length;
  }, [parsedAirdropInput.items, airdropActiveLine]);

  const airdropActiveLineHasContent = useMemo(() => {
    const rawLine = airdropRawLines[airdropActiveLine - 1] ?? "";
    return rawLine.trim().length > 0;
  }, [airdropRawLines, airdropActiveLine]);

  const getAirdropLineFromCursor = useCallback((value, cursorPos) => {
    const safePos = Math.max(0, Math.min(cursorPos ?? 0, value.length));
    return value.slice(0, safePos).split(/\r?\n/).length;
  }, []);

  const syncAirdropActiveLine = useCallback((target) => {
    if (!target) return;
    const nextLine = getAirdropLineFromCursor(target.value || "", target.selectionStart ?? 0);
    setAirdropActiveLine(nextLine);
  }, [getAirdropLineFromCursor]);

  const syncAirdropScroll = useCallback((target) => {
    const top = target?.scrollTop || 0;
    setAirdropScrollTop(top);
    if (airdropGutterRef.current) airdropGutterRef.current.style.transform = `translateY(${-top}px)`;
  }, []);

  const handleAirdropShowExample = useCallback(() => {
    const sampleEqual = [
      "0x059Da6A5aDb2e07946c8A0e7bb80cC63d62de67d",
      "0x813Ad0f745d35EBab457843D5641a9A9913c6ba7",
      "0xDC2FbE78c78Dcd4FE3E4edA68afd056c47208bCA",
    ];
    const sampleDifferent = [
      "0x059Da6A5aDb2e07946c8A0e7bb80cC63d62de67d, 0.15",
      "0x813Ad0f745d35EBab457843D5641a9A9913c6ba7, 2",
      "0xDC2FbE78c78Dcd4FE3E4edA68afd056c47208bCA, 0.02",
    ];
    setAirdropRowsText((airdropMode === "different" ? sampleDifferent : sampleEqual).join("\n"));
    setAirdropActiveLine(1);
  }, [airdropMode]);

  async function handleLockFileUpload(event) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setLockError("File too large. Max size is 2MB.");
      return;
    }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["csv", "txt"].includes(ext)) {
      setLockError("Unsupported file type. Use CSV or TXT.");
      return;
    }
    try {
      const text = await file.text();
      setLockRowsText(text.replace(/;/g, ","));
    } catch {
      setLockError("Could not read file.");
    }
  }

  async function handleCreateLock() {
    if (!walletClient || !publicClient || !address || !lockTokenMeta || !lockTokenLooksValid) return;
    if (!isAddress(LOCKER_CONTRACT_ADDRESS)) {
      setLockError("Lock contract address is missing or invalid.");
      return;
    }
    if (!lockUnlockTimestamp) {
      setLockError("Select a valid unlock schedule.");
      return;
    }
    if (lockUnlockTimestamp <= Math.floor(Date.now() / 1000)) {
      setLockError("Unlock date must be in the future.");
      return;
    }
    if (lockType === "quick" && !isAddress(lockEffectiveRecipient || "")) {
      setLockError("Enter a valid recipient wallet.");
      return;
    }
    if (lockType === "time" && lockCalc.count <= 0) {
      setLockError("Add at least one valid recipient.");
      return;
    }

    setLockBusy(true);
    setLockError("");
    setLockSuccess("");
    try {
      if (lockNeedsApproval) {
        const approveHash = await walletClient.writeContract({
          address: normalizedLockToken,
          abi: erc20Abi,
          functionName: "approve",
          args: [LOCKER_CONTRACT_ADDRESS, lockRequiredAllowanceRaw],
          account: address,
        });
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
      }

      let hash;
      if (lockType === "quick") {
        hash = await walletClient.writeContract({
          address: LOCKER_CONTRACT_ADDRESS,
          abi: LOCKER_ABI,
          functionName: "createQuickLock",
          args: [normalizedLockToken, lockCalc.totalRaw, parseUnits(String(lockUnlockTimestamp), 0), lockEffectiveRecipient],
          value: lockCreateFeeWei,
          account: address,
        });
      } else if (lockParsedRows.length > 0) {
        hash = await walletClient.writeContract({
          address: LOCKER_CONTRACT_ADDRESS,
          abi: LOCKER_ABI,
          functionName: "createTimeLockBatch",
          args: [
            normalizedLockToken,
            lockCalc.batchRecipients,
            lockCalc.batchAmounts,
            parseUnits(String(lockUnlockTimestamp), 0),
            lockTransferable,
          ],
          value: lockCreateFeeWei * parseUnits(String(lockCalc.count), 0),
          account: address,
        });
      } else {
        hash = await walletClient.writeContract({
          address: LOCKER_CONTRACT_ADDRESS,
          abi: LOCKER_ABI,
          functionName: "createTimeLock",
          args: [
            normalizedLockToken,
            lockCalc.totalRaw,
            parseUnits(String(lockUnlockTimestamp), 0),
            lockEffectiveRecipient,
            lockTransferable,
          ],
          value: lockCreateFeeWei,
          account: address,
        });
      }

      await publicClient.waitForTransactionReceipt({ hash });
      setLockSuccess(`Lock created. Tx: ${hash}`);
      await refreshLockTokenMeta(normalizedLockToken);
    } catch (err) {
      setLockError(err?.shortMessage || err?.message || "Create lock failed.");
    } finally {
      setLockBusy(false);
    }
  }

  async function handleCreateLottery() {
    if (!walletClient || !publicClient || !address) return;
    if (!isAddress(LOTTERY_CONTRACT_ADDRESS)) {
      setLotteryError("Lottery contract address is missing or invalid.");
      return;
    }
    setLotteryBusy(true);
    setLotteryError("");
    setLotterySuccess("");
    try {
      if (lotteryNeedsApprovePrize && lotteryPrizeTokenValid) {
        const approveHash = await walletClient.writeContract({
          address: normalizedLotteryPrizeToken,
          abi: erc20Abi,
          functionName: "approve",
          args: [LOTTERY_CONTRACT_ADDRESS, lotteryPrizeAmountRaw],
          account: address,
        });
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
      }

      const winnerBps = lotteryWinnerSplitArray.map((v) => Math.floor(v * 100));
      const hash = await walletClient.writeContract({
        address: LOTTERY_CONTRACT_ADDRESS,
        abi: LOTTERY_ABI,
        functionName: "createRaffle",
        args: [
          normalizedLotteryPrizeToken,
          lotteryPrizeAmountRaw,
          lotteryEntryTokenMode === "native" ? "0x0000000000000000000000000000000000000000" : normalizedLotteryEntryToken,
          lotteryEntryCostRaw,
          parseUnits(String(lotteryEndTs), 0),
          parseUnits(String(Math.floor(Number(lotteryMinEntries || 0))), 0),
          parseUnits(String(lotteryWinnerCountNum), 0),
          winnerBps,
          Math.floor(lotteryCreatorFee),
          Math.floor(lotteryBurnFee),
          Math.floor(lotteryPotFee),
          lotteryCreatorRecipient,
          lotteryBurnWallet,
        ],
        account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash });
      setLotterySuccess(`Lottery created. Tx: ${hash}`);
      await refreshLotteryPrizeMeta(normalizedLotteryPrizeToken);
    } catch (err) {
      setLotteryError(err?.shortMessage || err?.message || "Create lottery failed.");
    } finally {
      setLotteryBusy(false);
    }
  }

  async function handleCreateStakingPool() {
    if (!walletClient || !publicClient || !address || !stakeTokenMeta) return;
    if (!isAddress(STAKING_CONTRACT_ADDRESS)) {
      setStakeError("Staking contract address is missing or invalid.");
      return;
    }
    setStakeBusy(true);
    setStakeError("");
    setStakeSuccess("");
    try {
      if (stakeNeedsApproval) {
        const approveHash = await walletClient.writeContract({
          address: normalizedStakeToken,
          abi: erc20Abi,
          functionName: "approve",
          args: [STAKING_CONTRACT_ADDRESS, stakeRewardAmountRaw],
          account: address,
        });
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
      }

      const lockDurations = stakeUseLocks
        ? stakeTierPairs.map((t) => Math.floor(t.days * 24 * 60 * 60))
        : [];
      const bonusBps = stakeUseLocks ? stakeTierPairs.map((t) => Math.floor(t.bonusBps)) : [];
      const nowSec = Math.floor(Date.now() / 1000);
      const startTime = nowSec + 60;

      const hash = await walletClient.writeContract({
        address: STAKING_CONTRACT_ADDRESS,
        abi: STAKING_ABI,
        functionName: "createPool",
        args: [
          normalizedStakeToken,
          normalizedStakeToken,
          stakeRewardAmountRaw,
          parseUnits(String(startTime), 0),
          parseUnits(String(stakeDurationSeconds), 0),
          stakeUseLocks,
          lockDurations,
          bonusBps,
          stakeUseLocks ? 0 : Math.floor(stakePenaltyBpsNum),
          stakeUseLocks ? "0x0000000000000000000000000000000000000000" : stakePenaltyRecipientResolved,
        ],
        account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash });
      setStakeSuccess(`Staking pool created. Tx: ${hash}`);
      await refreshStakeTokenMeta(normalizedStakeToken);
    } catch (err) {
      setStakeError(err?.shortMessage || err?.message || "Create staking pool failed.");
    } finally {
      setStakeBusy(false);
    }
  }

  async function handleAirdrop() {
    if (!walletClient || !publicClient || !address || !airdropTokenMeta || !airdropTokenLooksValid) return;
    if (isWrongNetwork) {
      setAirdropError(`Wrong network. Switch to Monad (chain ${EXPECTED_MONAD_CHAIN_ID}).`);
      return;
    }
    if (!isAddress(AIRDROP_CONTRACT_ADDRESS)) {
      setAirdropError("Airdrop contract address is missing or invalid.");
      return;
    }
    if (!isAddress(AIRDROP_MANAGER_CONTRACT_ADDRESS)) {
      setAirdropError("Airdrop manager contract address is missing or invalid.");
      return;
    }
    const { recipients, amountsRaw, amountEachRaw } = airdropCallData;
    if (!recipients.length) {
      setAirdropError("Add at least one valid recipient.");
      return;
    }
    if (parsedAirdropInput.invalidCount > 0) {
      setAirdropError(`Fix invalid rows first (${parsedAirdropInput.invalidCount}).`);
      return;
    }
    if (parsedAirdropInput.duplicateCount > 0) {
      setAirdropError(`Remove duplicate recipient addresses first (${parsedAirdropInput.duplicateCount}).`);
      return;
    }

    setAirdropBusy(true);
    setAirdropBusyLabel(airdropNeedsApproval ? "Waiting for approval..." : "Preparing Transactions");
    setAirdropError("");
    setAirdropSuccess("");
    setAirdropProgressRows([]);
    setAirdropFirstTxHash("");

    try {
      if (airdropNeedsApproval) {
        setAirdropBusyLabel("Waiting for approval...");
        const approveHash = await walletClient.writeContract({
          address: normalizedAirdropToken,
          abi: erc20Abi,
          functionName: "approve",
          args: [AIRDROP_MANAGER_CONTRACT_ADDRESS, airdropCallData.totalRaw],
          account: address,
        });
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
      }
      setAirdropBusyLabel("Preparing Transactions");

      let livePlanQuote = airdropPlanQuote;
      try {
        const liveNonce = await publicClient.readContract({
          address: AIRDROP_MANAGER_CONTRACT_ADDRESS,
          abi: AIRDROP_MANAGER_ABI,
          functionName: "userNextNonce",
          args: [address],
        });
        const quoteUrl = new URL(`${TOOLS_API_BASE}/api/tools/airdrop-plan-quote`);
        quoteUrl.searchParams.set("user", address);
        quoteUrl.searchParams.set("token", normalizedAirdropToken);
        quoteUrl.searchParams.set("totalTokenAmountRaw", String(airdropCallData.totalRaw));
        quoteUrl.searchParams.set("recipients", String(airdropCallData.recipients.length));
        quoteUrl.searchParams.set("nonce", String(liveNonce));
        const quoteRes = await fetch(quoteUrl.toString());
        const quoteJson = await quoteRes.json().catch(() => null);
        if (!quoteRes.ok || !quoteJson?.ok || !quoteJson?.signature || !quoteJson?.feeWei || !quoteJson?.deadline) {
          throw new Error(quoteJson?.error || `Plan quote failed (${quoteRes.status})`);
        }
        livePlanQuote = quoteJson;
        setAirdropPlanQuote(quoteJson);
      } catch (quoteErr) {
        throw new Error(quoteErr?.message || "Fee quote unavailable. Please try again.");
      }

      const quotedFeeWei = parseUnits(String(livePlanQuote.feeWei), 0);
      const createPlanHash = await walletClient.writeContract({
        address: AIRDROP_MANAGER_CONTRACT_ADDRESS,
        abi: AIRDROP_MANAGER_ABI,
        functionName: "createPlan",
        args: [
          normalizedAirdropToken,
          airdropCallData.totalRaw,
          Number(airdropCallData.recipients.length),
          quotedFeeWei,
          Number(livePlanQuote.deadline),
          livePlanQuote.signature,
        ],
        account: address,
        value: quotedFeeWei,
      });
      const createPlanReceipt = await publicClient.waitForTransactionReceipt({ hash: createPlanHash });
      const createPlanLog = createPlanReceipt?.logs?.find((log) => String(log?.address || "").toLowerCase() === AIRDROP_MANAGER_CONTRACT_ADDRESS.toLowerCase());
      if (!createPlanLog?.topics?.[1]) {
        throw new Error("Failed to resolve created plan id");
      }
      const planId = Number.parseInt(String(createPlanLog.topics[1]), 16);
      if (!Number.isFinite(planId) || planId <= 0) {
        throw new Error("Invalid plan id from createPlan receipt");
      }

      let batchSize = Math.min(DEFAULT_AIRDROP_BATCH_SIZE, recipients.length);
      const progress = [];
      let cursor = 0;

      while (cursor < recipients.length) {
        const end = Math.min(cursor + batchSize, recipients.length);
        const batchRecipients = recipients.slice(cursor, end);
        const batchAmounts = airdropMode === "different" ? amountsRaw.slice(cursor, end) : [];
        const batchIndex = progress.length + 1;
        const batchLabel = `Batch ${batchIndex} (${cursor + 1}-${end} of ${recipients.length})`;

        try {
          if (cursor === 0) setAirdropBusyLabel("Preparing Transactions");
          const hash = await walletClient.writeContract({
            address: AIRDROP_MANAGER_CONTRACT_ADDRESS,
            abi: AIRDROP_MANAGER_ABI,
            functionName: airdropMode === "equal" ? "executeBatchEqual" : "executeBatchDifferent",
            args: airdropMode === "equal"
              ? [planId, batchRecipients, amountEachRaw]
              : [planId, batchRecipients, batchAmounts],
            account: address,
          });
          if (end >= recipients.length) {
            setAirdropBusyLabel("Waiting for confirmation...");
          }
          await publicClient.waitForTransactionReceipt({ hash });
          if (end >= recipients.length) {
            setAirdropBusyLabel("Confirming...");
          }
          progress.push({ label: batchLabel, ok: true, hash });
          setAirdropProgressRows([...progress]);
          cursor = end;
        } catch (batchErr) {
          const message = batchErr?.shortMessage || batchErr?.message || "Batch failed";
          if (/out of gas|intrinsic gas too low|gas required exceeds allowance/i.test(message) && batchSize > MIN_AIRDROP_BATCH_SIZE) {
            batchSize = Math.max(MIN_AIRDROP_BATCH_SIZE, Math.floor(batchSize / 2));
            progress.push({ label: `${batchLabel} (retrying smaller)`, ok: false, hash: "", error: `Resizing batch to ${batchSize}` });
            setAirdropProgressRows([...progress]);
            continue;
          }
          progress.push({ label: batchLabel, ok: false, hash: "", error: message });
          setAirdropProgressRows([...progress]);
          throw new Error(`Stopped at ${batchLabel}: ${message}`);
        }
      }

      const successCount = progress.filter((p) => p.ok).length;
      const finalizeHash = await walletClient.writeContract({
        address: AIRDROP_MANAGER_CONTRACT_ADDRESS,
        abi: AIRDROP_MANAGER_ABI,
        functionName: "finalizePlan",
        args: [planId],
        account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: finalizeHash });
      const firstConfirmed = progress.find((p) => p.ok && p.hash);
      setAirdropFirstTxHash(firstConfirmed?.hash || "");
      setAirdropSuccess(`Airdrop finished. ${successCount}/${progress.length} batches confirmed.`);
      await refreshAirdropTokenMeta(normalizedAirdropToken);
    } catch (err) {
      setAirdropError(err?.shortMessage || err?.message || "Airdrop failed.");
    } finally {
      setAirdropBusy(false);
      setAirdropBusyLabel("");
    }
  }

  async function handleApproveOrBurn() {
    if (!walletClient || !publicClient || !address || !burnTokenMeta || !burnAmountRaw || !burnTokenLooksValid) return;
    if (!isAddress(BURN_CONTRACT_ADDRESS)) {
      setBurnError("Burn contract address is missing or invalid.");
      return;
    }

    setBurnTxBusy(true);
    setBurnError("");
    setBurnSuccess("");

    try {
      if (needsApproval) {
        const approveHash = await walletClient.writeContract({
          address: normalizedBurnToken,
          abi: erc20Abi,
          functionName: "approve",
          args: [BURN_CONTRACT_ADDRESS, burnAmountRaw],
          account: address,
        });
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
      }

      const burnAbi = [
        {
          type: "function",
          name: "burn",
          stateMutability: "nonpayable",
          inputs: [
            { name: "token", type: "address" },
            { name: "amount", type: "uint256" },
          ],
          outputs: [],
        },
      ];

      const burnHash = await walletClient.writeContract({
        address: BURN_CONTRACT_ADDRESS,
        abi: burnAbi,
        functionName: "burn",
        args: [normalizedBurnToken, burnAmountRaw],
        account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: burnHash });

      setBurnSuccess(`Burn complete. Tx: ${burnHash}`);
      await refreshBurnTokenMeta(normalizedBurnToken);
      setBurnAmount("");
    } catch (err) {
      setBurnError(err?.shortMessage || err?.message || "Transaction failed.");
    } finally {
      setBurnTxBusy(false);
    }
  }

  useEffect(() => {
    setBurnError("");
    setBurnSuccess("");
    if (!burnTokenLooksValid || !isConnected || !address) {
      setBurnTokenMeta(null);
      setBurnAllowanceRaw(0n);
      return;
    }
    refreshBurnTokenMeta(normalizedBurnToken);
  }, [normalizedBurnToken, burnTokenLooksValid, isConnected, address, BURN_CONTRACT_ADDRESS, refreshBurnTokenMeta]);

  useEffect(() => {
    setAirdropError("");
    setAirdropSuccess("");
    if (!airdropTokenLooksValid || !isConnected || !address) {
      setAirdropTokenMeta(null);
      setAirdropAllowanceRaw(0n);
      return;
    }
    refreshAirdropTokenMeta(normalizedAirdropToken);
  }, [normalizedAirdropToken, airdropTokenLooksValid, isConnected, address, AIRDROP_CONTRACT_ADDRESS, refreshAirdropTokenMeta]);

  useEffect(() => {
    let cancelled = false;
    async function loadNativeBalance() {
      if (!publicClient || !address || !isConnected) {
        if (!cancelled) setAirdropNativeBalanceRaw(0n);
        return;
      }
      try {
        const bal = await publicClient.getBalance({ address });
        if (!cancelled) setAirdropNativeBalanceRaw(bal || 0n);
      } catch {
        if (!cancelled) setAirdropNativeBalanceRaw(0n);
      }
    }
    loadNativeBalance();
    return () => { cancelled = true; };
  }, [publicClient, address, isConnected, chainId]);

  useEffect(() => {
    setLockError("");
    setLockSuccess("");
    if (!lockTokenLooksValid || !isConnected || !address) {
      setLockTokenMeta(null);
      setLockAllowanceRaw(0n);
      return;
    }
    refreshLockTokenMeta(normalizedLockToken);
  }, [normalizedLockToken, lockTokenLooksValid, isConnected, address, LOCKER_CONTRACT_ADDRESS, refreshLockTokenMeta]);

  useEffect(() => {
    setLotteryError("");
    setLotterySuccess("");
    if (!lotteryPrizeTokenValid || !isConnected || !address) {
      setLotteryPrizeMeta(null);
      setLotteryPrizeAllowanceRaw(0n);
      return;
    }
    refreshLotteryPrizeMeta(normalizedLotteryPrizeToken);
  }, [normalizedLotteryPrizeToken, lotteryPrizeTokenValid, isConnected, address, LOTTERY_CONTRACT_ADDRESS, refreshLotteryPrizeMeta]);

  useEffect(() => {
    if (lotteryEntryTokenMode === "native") {
      setLotteryEntryMeta({ symbol: "MON", name: "Monad", decimals: 18, balanceRaw: 0n, balanceFormatted: "-" });
      return;
    }
    if (!lotteryEntryTokenValid || !isConnected || !address) {
      setLotteryEntryMeta(null);
      return;
    }
    refreshLotteryEntryMeta(normalizedLotteryEntryToken);
  }, [lotteryEntryTokenMode, normalizedLotteryEntryToken, lotteryEntryTokenValid, isConnected, address, refreshLotteryEntryMeta]);

  useEffect(() => {
    setStakeError("");
    setStakeSuccess("");
    if (!stakeTokenLooksValid || !isConnected || !address) {
      setStakeTokenMeta(null);
      setStakeAllowanceRaw(0n);
      return;
    }
    refreshStakeTokenMeta(normalizedStakeToken);
  }, [normalizedStakeToken, stakeTokenLooksValid, isConnected, address, refreshStakeTokenMeta]);

  return (
    <div id="bob-tools-page">
      <div className="bt-bg" aria-hidden="true" style={{ "--bt-bg-image": 'url("/landing/bobspace.webp")' }} />
      <Nav useSmoothScroll={false} />

      <main className="bt-main">
        <section className="bt-page-tabs-sticky" aria-label="Tools section tabs">
          <div className="bt-page-tabs-card">
            <div className="bt-page-tabs" role="tablist" aria-label="Tools sections">
              {TAB_CONFIG.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`bt-page-tab-btn bt-page-tab-btn-${tab.key} ${pageTab === tab.key ? "is-active" : ""}`}
                  onClick={() => setPageTab(tab.key)}
                >
                  <span className="bt-tab-icon" style={{ "--tab-icon": `url('${tab.icon}')` }} aria-hidden="true" />
                  <span className="bt-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="bt-content-stack">
          <section className="bt-card bt-section-card">
            <div className="bt-header-stack">
              <h1>{activeTab.title} <span className="bt-accent">{activeTab.key === "airdrop" ? "TOOL" : "TOOLS"}</span></h1>
              <p>{activeTab.subtitle}</p>
            </div>
            {activeTab.key !== "airdrop" ? (
              <div className="bt-subtabs-shell">
                <div className="bt-subtabs" role="tablist" aria-label={`${activeTab.label} views`}>
                  <button
                    type="button"
                    className={`bt-mode-btn ${activeToolSubview === "list" ? "is-active" : ""}`}
                    onClick={() => setToolSubtabs((prev) => ({ ...prev, [pageTab]: "list" }))}
                  >
                    {TOOL_SUBTAB_LABELS[pageTab]?.list || "Recent"}
                  </button>
                  <button
                    type="button"
                    className={`bt-mode-btn ${activeToolSubview === "create" ? "is-active" : ""}`}
                    onClick={() => setToolSubtabs((prev) => ({ ...prev, [pageTab]: "create" }))}
                  >
                    {TOOL_SUBTAB_LABELS[pageTab]?.create || "Create"}
                  </button>
                </div>
              </div>
            ) : null}
            {activeTab.key === "stake" ? (
              activeToolSubview === "create" ? (
              <div className="bt-burn-shell">
                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Token address</label>
                    <p className="bt-field-help">ERC-20 token for staking + rewards.</p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={stakeTokenAddress}
                      onChange={(e) => setStakeTokenAddress(e.target.value)}
                    />
                    {stakeTokenMeta ? (
                      <div className="bt-token-meta">
                        <p className="bt-token-name">
                          {stakeTokenMeta.name} ({stakeTokenMeta.symbol})
                        </p>
                        <p className="bt-token-balance">
                          Your balance
                          <span>{stakeTokenMeta.balanceFormatted} {stakeTokenMeta.symbol}</span>
                        </p>
                      </div>
                    ) : null}
                  </div>
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Reward pool amount</label>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0.0"
                      value={stakeRewardAmount}
                      onChange={(e) => setStakeRewardAmount(e.target.value)}
                    />
                    <label className="bt-field-label">Pool length (days)</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="1"
                      value={stakeDurationDays}
                      onChange={(e) => setStakeDurationDays(e.target.value)}
                    />
                    <label className="bt-field-label">Minimum stake (display note)</label>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="optional"
                      value={stakeMinStake}
                      onChange={(e) => setStakeMinStake(e.target.value)}
                    />
                  </div>
                </div>

                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Lock tiers</label>
                    <div className="bt-mode-row">
                      <button
                        type="button"
                        className={`bt-mode-btn ${stakeUseLocks ? "is-active" : ""}`}
                        onClick={() => setStakeUseLocks(true)}
                      >
                        Enabled
                      </button>
                      <button
                        type="button"
                        className={`bt-mode-btn ${!stakeUseLocks ? "is-active" : ""}`}
                        onClick={() => setStakeUseLocks(false)}
                      >
                        Disabled
                      </button>
                    </div>
                    {stakeUseLocks ? (
                      <>
                        <label className="bt-field-label">Tiers (days:bonusBps)</label>
                        <p className="bt-field-help">Example: 7:500 = 1 week lock, +5% reward boost</p>
                        <textarea
                          className="bt-input bt-textarea"
                          value={stakeLockTiersText}
                          onChange={(e) => setStakeLockTiersText(e.target.value)}
                          placeholder={"7:500\n14:1200\n30:2500"}
                        />
                      </>
                    ) : null}
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Early unstake penalty (no-lock mode)</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="0"
                      max="10000"
                      value={stakePenaltyBps}
                      onChange={(e) => setStakePenaltyBps(e.target.value)}
                      disabled={stakeUseLocks}
                    />
                    <p className="bt-field-help">Bps: 500 = 5%. Applies only before pool end.</p>
                    <label className="bt-check-row">
                      <input
                        type="checkbox"
                        checked={stakeUseConnectedRecipient}
                        onChange={(e) => setStakeUseConnectedRecipient(e.target.checked)}
                        disabled={stakeUseLocks}
                      />
                      <span>Use connected wallet for penalty recipient</span>
                    </label>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={stakePenaltyRecipient}
                      onChange={(e) => setStakePenaltyRecipient(e.target.value)}
                      disabled={stakeUseLocks || stakeUseConnectedRecipient}
                    />
                  </div>
                </div>

                <div className="bt-burn-card">
                  <label className="bt-field-label">Review</label>
                  <div className="bt-stats-row">
                    <span>Token: {stakeTokenMeta?.symbol || "-"}</span>
                    <span>Rewards: {stakeRewardAmount || "0"} {stakeTokenMeta?.symbol || ""}</span>
                    <span>Duration: {stakeDurationDays || "0"} days</span>
                    <span>Locks: {stakeUseLocks ? "Enabled" : "Disabled"}</span>
                    <span>Tiers: {stakeUseLocks ? stakeTierPairs.length : 0}</span>
                    <span>Penalty: {stakeUseLocks ? "0" : `${stakePenaltyBpsNum} bps`}</span>
                    <span>Penalty recipient: {stakeUseLocks ? "-" : truncateAddr(stakePenaltyRecipientResolved)}</span>
                    <span>Min stake note: {stakeMinStake || "-"}</span>
                  </div>
                  <button
                    type="button"
                    className="bt-burn-btn"
                    disabled={stakeDisabled}
                    onClick={handleCreateStakingPool}
                  >
                    {stakeBusy ? "Processing..." : stakeNeedsApproval ? "Approve + Create Pool" : "Create Staking Pool"}
                  </button>
                </div>

                {!isAddress(STAKING_CONTRACT_ADDRESS) ? (
                  <p className="bt-status bt-status-error">Missing `REACT_APP_BOB_STAKING_CONTRACT_ADDRESS`.</p>
                ) : null}
                {stakeError ? <p className="bt-status bt-status-error">{stakeError}</p> : null}
                {stakeSuccess ? <p className="bt-status bt-status-ok">{stakeSuccess}</p> : null}
              </div>
              ) : (
                <div className="bt-under-construction">
                  <span className="bt-under-construction-pill">Active Pools</span>
                  <p className="bt-field-help">Your created and currently running staking pools will appear here.</p>
                </div>
              )
            ) : activeTab.key === "lock" ? (
              activeToolSubview === "create" ? (
              <div className="bt-burn-shell">
                <div className="bt-burn-card">
                  <label className="bt-field-label">Lock type</label>
                  <div className="bt-mode-row">
                    <button
                      type="button"
                      className={`bt-mode-btn ${lockType === "quick" ? "is-active" : ""}`}
                      onClick={() => setLockType("quick")}
                    >
                      Quick lock
                    </button>
                    <button
                      type="button"
                      className={`bt-mode-btn ${lockType === "time" ? "is-active" : ""}`}
                      onClick={() => setLockType("time")}
                    >
                      Time-based lock
                    </button>
                  </div>
                </div>

                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Token</label>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={lockTokenAddress}
                      onChange={(e) => setLockTokenAddress(e.target.value)}
                    />
                    {lockLoadingMeta ? <p className="bt-inline-info">Loading token metadata...</p> : null}
                    {lockTokenMeta ? (
                      <div className="bt-token-meta">
                        <p className="bt-token-name">{lockTokenMeta.name} ({lockTokenMeta.symbol})</p>
                        <p className="bt-token-balance">
                          Your balance
                          <span>{lockTokenMeta.balanceFormatted} {lockTokenMeta.symbol}</span>
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Unlock schedule</label>
                    <input
                      className="bt-input"
                      type="datetime-local"
                      value={lockUnlockAt}
                      onChange={(e) => setLockUnlockAt(e.target.value)}
                    />
                    <p className="bt-field-help">Select date & time for unlock.</p>
                  </div>
                </div>

                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Amount</label>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0.0"
                      value={lockAmount}
                      onChange={(e) => setLockAmount(e.target.value)}
                    />
                    <p className="bt-field-help">Used for quick lock or single-recipient time lock.</p>
                  </div>
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Wallet address</label>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={lockRecipient}
                      onChange={(e) => setLockRecipient(e.target.value)}
                      disabled={lockUseConnectedWallet}
                    />
                    <label className="bt-check-row">
                      <input
                        type="checkbox"
                        checked={lockUseConnectedWallet}
                        onChange={(e) => {
                          setLockUseConnectedWallet(e.target.checked);
                          if (e.target.checked && address) setLockRecipient(address);
                        }}
                      />
                      <span>Use connected wallet</span>
                    </label>
                    {lockType === "time" ? (
                      <label className="bt-check-row">
                        <input
                          type="checkbox"
                          checked={lockTransferable}
                          onChange={(e) => setLockTransferable(e.target.checked)}
                        />
                        <span>Transferable</span>
                      </label>
                    ) : null}
                  </div>
                </div>

                {lockType === "time" ? (
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Recipients (optional batch)</label>
                    <p className="bt-field-help">One per line: `0xabc..., 0.5`</p>
                    <textarea
                      className="bt-input bt-textarea"
                      value={lockRowsText}
                      onChange={(e) => setLockRowsText(e.target.value)}
                      placeholder={"0x059Da6A5aDb2e07946c8A0e7bb80cC63d62de67d, 0.15\n0x813Ad0f745d35EBab457843D5641a9A9913c6ba7, 2"}
                    />
                    <div className="bt-upload-row">
                      <input type="file" accept=".csv,.txt" onChange={handleLockFileUpload} />
                      <span className="bt-inline-info">Max size: 2MB (CSV or TXT)</span>
                    </div>
                  </div>
                ) : null}

                <div className="bt-burn-card">
                  <label className="bt-field-label">Review</label>
                  <p className="bt-field-help">
                    Token locks are irreversible and cannot be accessed before unlock.
                  </p>
                  <div className="bt-stats-row">
                    <span>Type: {lockType === "quick" ? "Quick lock" : "Time-based lock"}</span>
                    <span>Recipients: {lockCalc.count}</span>
                    <span>Locked amount: {lockCalc.totalText}</span>
                    <span>Unlock: {lockUnlockTimestamp ? new Date(lockUnlockTimestamp * 1000).toLocaleString() : "-"}</span>
                    <span>Token fee ({(lockTokenFeeBps / 100).toFixed(2)}%): {lockTokenMeta ? `${formatUnits(lockTokenFeeRaw, lockTokenMeta.decimals)} ${lockTokenMeta.symbol}` : "-"}</span>
                    <span>Create lock fee: {lockNativeFeeDisplay}</span>
                    <span>Recipient: {truncateAddr(lockEffectiveRecipient)}</span>
                  </div>
                  <button
                    type="button"
                    className="bt-burn-btn"
                    disabled={lockDisabled}
                    onClick={handleCreateLock}
                  >
                    {lockBusy ? "Processing..." : lockNeedsApproval ? "Approve + Create Lock" : "Create Lock"}
                  </button>
                </div>

                {!isAddress(LOCKER_CONTRACT_ADDRESS) ? (
                  <p className="bt-status bt-status-error">Missing `REACT_APP_BOB_LOCKER_CONTRACT_ADDRESS`.</p>
                ) : null}
                {lockError ? <p className="bt-status bt-status-error">{lockError}</p> : null}
                {lockSuccess ? <p className="bt-status bt-status-ok">{lockSuccess}</p> : null}
              </div>
              ) : (
                <div className="bt-under-construction">
                  <span className="bt-under-construction-pill">Active Lockers</span>
                  <p className="bt-field-help">Your active lock contracts and status will appear here.</p>
                </div>
              )
            ) : activeTab.key === "burn" ? (
              activeToolSubview === "create" ? (
              <div className="bt-burn-shell">
                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Token address</label>
                    <p className="bt-field-help">Enter the address of the ERC-20 token to start.</p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={burnTokenAddress}
                      onChange={(e) => setBurnTokenAddress(e.target.value)}
                    />
                    {burnLoadingMeta ? <p className="bt-inline-info">Loading token metadata...</p> : null}
                    {burnTokenMeta ? (
                      <div className="bt-token-meta">
                        <p className="bt-token-name">
                          {burnTokenMeta.name} ({burnTokenMeta.symbol})
                        </p>
                        <p className="bt-token-balance">
                          Your balance
                          <span>{burnTokenMeta.balanceFormatted} {burnTokenMeta.symbol}</span>
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Burn amount</label>
                    <p className="bt-field-help">Enter the amount to burn. We auto-detect token decimals.</p>
                    <p className="bt-inline-info">
                      Burn sends tokens to the dead wallet. Some ERC-20 tokens may not reduce on-chain totalSupply.
                    </p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0.0"
                      value={burnAmount}
                      onChange={(e) => setBurnAmount(e.target.value)}
                    />
                    <button
                      type="button"
                      className="bt-burn-btn"
                      disabled={burnButtonDisabled}
                      onClick={handleApproveOrBurn}
                    >
                      {burnTxBusy ? "Processing..." : needsApproval ? "Approve + Burn" : "Burn"}
                    </button>
                  </div>
                </div>

                {!BURN_CONTRACT_ADDRESS ? (
                  <p className="bt-status bt-status-error">Missing `REACT_APP_BOB_BURN_CONTRACT_ADDRESS`.</p>
                ) : null}
                {burnError ? <p className="bt-status bt-status-error">{burnError}</p> : null}
                {burnSuccess ? <p className="bt-status bt-status-ok">{burnSuccess}</p> : null}
              </div>
              ) : (
                <div className="bt-under-construction">
                  <span className="bt-under-construction-pill">Recent Burns</span>
                  <p className="bt-field-help">Recent burn transactions and links will appear here.</p>
                </div>
              )
            ) : activeTab.key === "airdrop" ? (
              activeToolSubview === "create" ? (
              <div className="bt-burn-shell">
                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Token address</label>
                    <p className="bt-field-help">Enter the address of the ERC-20 token to start.</p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={airdropTokenAddress}
                      onChange={(e) => setAirdropTokenAddress(e.target.value)}
                    />
                    {airdropLoadingMeta ? <p className="bt-inline-info">Loading token metadata...</p> : null}
                    {normalizedAirdropToken ? (
                      <div className="bt-token-meta bt-token-meta-row">
                        <p className="bt-token-name bt-token-name-inline">
                          {airdropTokenMeta
                            ? `${airdropTokenMeta.name} (${airdropTokenMeta.symbol})`
                            : <span className="bt-token-placeholder">-</span>}
                        </p>
                        <p className="bt-token-balance-inline">
                          <span className="bt-token-balance-inline-label">Your Balance:</span>{" "}
                          <span className="bt-token-balance-inline-value">
                            {airdropTokenMeta
                              ? `${formatTokenBalanceDisplay(airdropTokenMeta.balanceFormatted)} ${airdropTokenMeta.symbol}`
                              : <span className="bt-token-placeholder">-</span>}
                          </span>
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Mode</label>
                    <p className="bt-field-help">Choose equal or different amounts per address.</p>
                    <div className="bt-mode-row bt-airdrop-mode-row">
                      <button
                        type="button"
                        className={`bt-mode-btn bt-airdrop-mode-btn ${airdropMode === "equal" ? "is-active" : ""}`}
                        onClick={() => setAirdropMode("equal")}
                      >
                        Equal amounts
                      </button>
                      <button
                        type="button"
                        className={`bt-mode-btn bt-airdrop-mode-btn ${airdropMode === "different" ? "is-active" : ""}`}
                        onClick={() => setAirdropMode("different")}
                      >
                        Different amounts
                      </button>
                    </div>
                    {airdropMode === "equal" ? (
                      <>
                        <label className="bt-field-label">Amount</label>
                        <p className="bt-field-help">Amount of tokens each address receives.</p>
                        <input
                          className="bt-input"
                          type="text"
                          placeholder="0.0"
                          value={airdropAmount}
                          onChange={(e) => setAirdropAmount(e.target.value)}
                        />
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="bt-burn-card">
                  <div className="bt-field-head">
                    <label className="bt-field-label">Addresses</label>
                    <button type="button" className="bt-inline-link" onClick={handleAirdropShowExample}>
                      Show example
                    </button>
                  </div>
                  <p className="bt-field-help">
                    {airdropMode === "equal"
                      ? "Provide all recipient addresses."
                      : "Provide all recipient addresses and amounts separated by comma."}
                  </p>
                  <div
                    className="bt-airdrop-textarea-wrap"
                    style={{ "--bt-airdrop-line-h": `${airdropLineHeightPx}px` }}
                  >
                    <div ref={airdropGutterRef} className="bt-airdrop-textarea-gutter" aria-hidden="true">
                      {Array.from({ length: airdropLineCount }, (_, i) => {
                        const parsedItem = parsedAirdropInput.items.find((it) => it.rowNumber === i + 1);
                        const rawLine = airdropRawLines[i] ?? "";
                        const rowHasContent = rawLine.trim().length > 0;
                        const rowHasErrors = !!parsedItem?.errors?.length;
                        const rowStateClass = rowHasErrors ? "is-error" : rowHasContent ? "is-ok" : "";
                        return (
                          <span
                            key={`airdrop-line-${i + 1}`}
                            className={`bt-airdrop-gutter-line ${rowStateClass} ${airdropActiveLine === i + 1 ? "is-active" : ""}`}
                          >
                            {i + 1}
                          </span>
                        );
                      })}
                    </div>
                    <span
                      aria-hidden="true"
                      className={`bt-airdrop-active-line-bg ${airdropActiveLineHasError ? "is-error" : airdropActiveLineHasContent ? "is-ok" : ""}`}
                      style={{ transform: `translateY(${(airdropActiveLine - 1) * airdropLineHeightPx - airdropScrollTop}px)` }}
                    />
                    <textarea
                      ref={airdropTextareaRef}
                      className="bt-input bt-textarea bt-airdrop-textarea"
                      placeholder=""
                      wrap="off"
                      value={airdropRowsText}
                      onChange={(e) => {
                        setAirdropRowsText(e.target.value);
                        syncAirdropActiveLine(e.target);
                      }}
                      onKeyUp={(e) => syncAirdropActiveLine(e.target)}
                      onClick={(e) => syncAirdropActiveLine(e.target)}
                      onSelect={(e) => syncAirdropActiveLine(e.target)}
                      onScroll={(e) => syncAirdropScroll(e.target)}
                    />
                  </div>
                  <div className="bt-airdrop-footer-row">
                    <div className="bt-airdrop-footer-left">
                      <div className="bt-file-picker">
                        <label className="bt-file-btn">
                          Choose File
                          <input
                            className="bt-file-input"
                            type="file"
                            accept=".csv,.txt"
                            onChange={handleAirdropFileUpload}
                          />
                        </label>
                        <span className="bt-file-name">{airdropFileName || "No file chosen"}</span>
                        <span className="bt-file-help">Max size: 2MB (CSV or TXT)</span>
                      </div>
                    </div>
                  </div>
                  {parsedAirdropInput.invalidCount > 0 ? (
                    <p className="bt-status bt-status-error">
                      Invalid rows detected: {parsedAirdropInput.invalidCount}. Fix before sending.
                    </p>
                  ) : null}
                  {parsedAirdropInput.duplicateCount > 0 ? (
                    <p className="bt-status bt-status-error">
                      Duplicate addresses detected: {parsedAirdropInput.duplicateCount}. Remove duplicates.
                    </p>
                  ) : null}
                  <p className="bt-inline-info">
                    Batched delivery is automatic. Very large drops are split into multiple transactions.
                  </p>
                  <p className="bt-inline-info">
                    Tax/blacklist/paused tokens may still fail per batch depending on token rules.
                  </p>
                  <button
                    type="button"
                    className="bt-burn-btn"
                    disabled={airdropDisabled}
                    onClick={handleAirdrop}
                  >
                    {airdropBusy
                      ? airdropBusyLabel || "Processing..."
                      : isWrongNetwork
                        ? "Wrong Network"
                      : airdropInsufficientBalance
                        ? "Insufficient Balance"
                        : airdropNeedsApproval
                          ? "Approve + Multisend"
                          : "Multisend"}
                  </button>
                  <div className={`bt-airdrop-stats-row ${airdropSuccess ? "is-completed" : ""}`}>
                    <span className="bt-airdrop-fee-item bt-airdrop-item-valid">
                      <span className="bt-token-balance-inline-label">Valid Addresses:</span>{" "}
                      <span className="bt-token-balance-inline-value">
                        {airdropRowsText.trim() ? parsedAirdropInput.validRows.length : "-"}
                      </span>
                    </span>
                    <span className="bt-airdrop-fee-item bt-airdrop-item-tokens">
                      <span className="bt-token-balance-inline-label">Tokens to send:</span>{" "}
                      <span className="bt-token-balance-inline-value">
                        {airdropEnteredTotalPreview && airdropEnteredTotalPreview !== "-" ? airdropEnteredTotalPreview : "-"}
                      </span>
                    </span>
                    <span className="bt-airdrop-fee-item bt-airdrop-item-status">
                      <span className="bt-token-balance-inline-label">Status:</span>{" "}
                      {airdropSuccess && airdropExplorerTxUrl ? (
                        <>
                          <a
                            href={airdropExplorerTxUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="bt-token-balance-inline-value is-status-complete bt-airdrop-status-link"
                            title="Open first batch transaction"
                          >
                            Completed
                          </a>
                          <a
                            href={airdropExplorerTxUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="bt-airdrop-status-explorer"
                            aria-label="View completed airdrop transaction on explorer"
                            title="View on Explorer"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M7 17L17 7" />
                              <path d="M9 7h8v8" />
                            </svg>
                          </a>
                        </>
                      ) : (
                        <span className={`bt-token-balance-inline-value ${airdropSuccess ? "is-status-complete" : ""}`}>
                          {airdropStatusValue}
                        </span>
                      )}
                    </span>
                    <span className="bt-airdrop-fee-item bt-airdrop-item-balance">
                      <span className="bt-token-balance-inline-label">Your balance:</span>{" "}
                      <span className="bt-token-balance-inline-value">
                        {isConnected ? `${formatMonDisplay(airdropNativeBalanceRaw, 2)} MON` : "-"}
                      </span>
                    </span>
                    <span className="bt-airdrop-fee-item bt-airdrop-item-service">
                      <span className="bt-token-balance-inline-label">Service fee:</span>{" "}
                      <span className="bt-token-balance-inline-value">
                        {showAirdropFeeValues
                          ? `${formatNumberToFixedLocale(airdropServiceFeeMon, 2)} MON (${formatNumberToFixedLocale(airdropServiceFeeUsd, 2)} USD)`
                          : "-"}
                      </span>
                    </span>
                    <span className="bt-airdrop-fee-item bt-airdrop-item-txfees">
                      <span className="bt-token-balance-inline-label">Transaction fees:</span>{" "}
                      <span className="bt-token-balance-inline-value">
                        {showAirdropFeeValues ? `~${formatNumberToFixedLocale(airdropEstimatedTxFeeMon, 2)} MON` : "-"}
                      </span>
                    </span>
                  </div>
                  {false && airdropProgressRows.length > 0 ? (
                    <div className="bt-token-meta">
                      <p className="bt-token-name">Check progress</p>
                      {airdropProgressRows.map((row, i) => (
                        <p key={`${row.label}-${i}`} className="bt-field-help">
                          {row.ok ? "OK" : "ERR"} {row.label}
                          {row.hash ? ` · ${row.hash}` : ""}
                          {row.error ? ` · ${row.error}` : ""}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>

                {!isAddress(AIRDROP_CONTRACT_ADDRESS) ? (
                  <p className="bt-status bt-status-error">Missing `REACT_APP_BOB_AIRDROP_CONTRACT_ADDRESS`.</p>
                ) : null}
                {airdropError ? <p className="bt-status bt-status-error">{airdropError}</p> : null}
                {airdropSuccess ? (
                  <p className="bt-status bt-status-ok">
                    {airdropSuccess}
                  </p>
                ) : null}
              </div>
              ) : (
                <div className="bt-under-construction">
                  <span className="bt-under-construction-pill">Recent Airdrops</span>
                  <p className="bt-field-help">Completed multisend activity and summaries will appear here.</p>
                </div>
              )
            ) : activeTab.key === "lottery" ? (
              activeToolSubview === "create" ? (
              <div className="bt-burn-shell">
                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Prize token address</label>
                    <p className="bt-field-help">ERC-20 token for rewards.</p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0x..."
                      value={lotteryPrizeToken}
                      onChange={(e) => setLotteryPrizeToken(e.target.value)}
                    />
                    {lotteryPrizeMeta ? (
                      <div className="bt-token-meta">
                        <p className="bt-token-name">
                          {lotteryPrizeMeta.name} ({lotteryPrizeMeta.symbol})
                        </p>
                        <p className="bt-token-balance">
                          Your balance
                          <span>{lotteryPrizeMeta.balanceFormatted} {lotteryPrizeMeta.symbol}</span>
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Prize amount</label>
                    <p className="bt-field-help">How much you add to the reward pool.</p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0.0"
                      value={lotteryPrizeAmount}
                      onChange={(e) => setLotteryPrizeAmount(e.target.value)}
                    />
                  </div>
                </div>

                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Entry token mode</label>
                    <div className="bt-mode-row">
                      <button
                        type="button"
                        className={`bt-mode-btn ${lotteryEntryTokenMode === "token" ? "is-active" : ""}`}
                        onClick={() => setLotteryEntryTokenMode("token")}
                      >
                        ERC-20 token
                      </button>
                      <button
                        type="button"
                        className={`bt-mode-btn ${lotteryEntryTokenMode === "native" ? "is-active" : ""}`}
                        onClick={() => setLotteryEntryTokenMode("native")}
                      >
                        MON (native)
                      </button>
                    </div>
                    {lotteryEntryTokenMode === "token" ? (
                      <>
                        <label className="bt-field-label">Entry token address</label>
                        <input
                          className="bt-input"
                          type="text"
                          placeholder="0x..."
                          value={lotteryEntryToken}
                          onChange={(e) => setLotteryEntryToken(e.target.value)}
                        />
                      </>
                    ) : null}
                    {lotteryEntryMeta ? (
                      <div className="bt-token-meta">
                        <p className="bt-token-name">
                          {lotteryEntryMeta.name} ({lotteryEntryMeta.symbol})
                        </p>
                        <p className="bt-token-balance">
                          Your balance
                          <span>{lotteryEntryMeta.balanceFormatted} {lotteryEntryMeta.symbol}</span>
                        </p>
                      </div>
                    ) : null}
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Entry cost</label>
                    <p className="bt-field-help">Cost per entry.</p>
                    <input
                      className="bt-input"
                      type="text"
                      placeholder="0.0"
                      value={lotteryEntryCost}
                      onChange={(e) => setLotteryEntryCost(e.target.value)}
                    />
                    <label className="bt-field-label">Duration (hours)</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="1"
                      value={lotteryDurationHours}
                      onChange={(e) => setLotteryDurationHours(e.target.value)}
                    />
                    <label className="bt-field-label">Minimum entries (or cancel + refund)</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="1"
                      value={lotteryMinEntries}
                      onChange={(e) => setLotteryMinEntries(e.target.value)}
                    />
                  </div>
                </div>

                <div className="bt-burn-grid">
                  <div className="bt-burn-card">
                    <label className="bt-field-label">Winners count</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="1"
                      value={lotteryWinnerCount}
                      onChange={(e) => setLotteryWinnerCount(e.target.value)}
                    />
                    <label className="bt-field-label">Winner splits (%)</label>
                    <p className="bt-field-help">Comma-separated. Example: 50,30,20</p>
                    <input
                      className="bt-input"
                      type="text"
                      value={lotteryWinnerSplits}
                      onChange={(e) => setLotteryWinnerSplits(e.target.value)}
                    />
                    <p className="bt-inline-info">
                      Parsed {lotteryWinnerSplitArray.length} winners, sum {lotteryWinnerSplitSum}%
                    </p>
                  </div>

                  <div className="bt-burn-card">
                    <label className="bt-field-label">Entry fee routing (bps)</label>
                    <p className="bt-field-help">Must total 10000 bps.</p>
                    <label className="bt-field-label">Creator</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="0"
                      max="10000"
                      value={lotteryCreatorFeeBps}
                      onChange={(e) => setLotteryCreatorFeeBps(e.target.value)}
                    />
                    <label className="bt-field-label">Burn</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="0"
                      max="10000"
                      value={lotteryBurnFeeBps}
                      onChange={(e) => setLotteryBurnFeeBps(e.target.value)}
                    />
                    <label className="bt-field-label">Pot</label>
                    <input
                      className="bt-input"
                      type="number"
                      min="0"
                      max="10000"
                      value={lotteryPotFeeBps}
                      onChange={(e) => setLotteryPotFeeBps(e.target.value)}
                    />
                    <p className="bt-inline-info">Total fee bps: {lotteryFeesSum}</p>
                  </div>
                </div>

                <div className="bt-burn-card">
                  <label className="bt-field-label">Recipients</label>
                  <label className="bt-check-row">
                    <input
                      type="checkbox"
                      checked={lotteryUseCreatorWallet}
                      onChange={(e) => setLotteryUseCreatorWallet(e.target.checked)}
                    />
                    <span>Use connected wallet as creator recipient</span>
                  </label>
                  {!lotteryUseCreatorWallet ? (
                    <>
                      <label className="bt-field-label">Creator recipient</label>
                      <input
                        className="bt-input"
                        type="text"
                        placeholder="0x..."
                        value={lotteryCreatorWallet}
                        onChange={(e) => setLotteryCreatorWallet(e.target.value)}
                      />
                    </>
                  ) : null}
                  <label className="bt-field-label">Burn recipient</label>
                  <input
                    className="bt-input"
                    type="text"
                    placeholder="0x000000000000000000000000000000000000dEaD"
                    value={lotteryBurnWallet}
                    onChange={(e) => setLotteryBurnWallet(e.target.value)}
                  />
                </div>

                <div className="bt-burn-card">
                  <label className="bt-field-label">Review</label>
                  <div className="bt-stats-row">
                    <span>End: {new Date(lotteryEndTs * 1000).toLocaleString()}</span>
                    <span>Entry token: {lotteryEntryTokenMode === "native" ? "MON" : (lotteryEntryMeta?.symbol || "ERC-20")}</span>
                    <span>Entry cost: {lotteryEntryCost || "0"}</span>
                    <span>Winners: {lotteryWinnerCountNum || 0}</span>
                    <span>Min entries: {lotteryMinEntries || "0"}</span>
                    <span>Prize token: {lotteryPrizeMeta?.symbol || "-"}</span>
                    <span>Prize amount: {lotteryPrizeAmount || "0"}</span>
                    <span>Creator/Burn/Pot bps: {lotteryCreatorFee}/{lotteryBurnFee}/{lotteryPotFee}</span>
                    <span>Creator recipient: {truncateAddr(lotteryCreatorRecipient)}</span>
                    <span>Burn recipient: {truncateAddr(lotteryBurnWallet)}</span>
                  </div>
                  <button
                    type="button"
                    className="bt-burn-btn"
                    disabled={lotteryDisabled}
                    onClick={handleCreateLottery}
                  >
                    {lotteryBusy ? "Processing..." : lotteryNeedsApprovePrize ? "Approve + Create Lottery" : "Create Lottery"}
                  </button>
                </div>

                {!isAddress(LOTTERY_CONTRACT_ADDRESS) ? (
                  <p className="bt-status bt-status-error">Missing `REACT_APP_BOB_LOTTERY_CONTRACT_ADDRESS`.</p>
                ) : null}
                {lotteryError ? <p className="bt-status bt-status-error">{lotteryError}</p> : null}
                {lotterySuccess ? <p className="bt-status bt-status-ok">{lotterySuccess}</p> : null}
              </div>
              ) : (
                <div className="bt-under-construction">
                  <span className="bt-under-construction-pill">Active Raffles</span>
                  <p className="bt-field-help">Live and completed raffle rounds will appear here.</p>
                </div>
              )
            ) : (
              <div className="bt-under-construction">
                <span className="bt-under-construction-pill">Under Construction</span>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
